import React, { useState, useCallback, useEffect, useMemo, useRef, memo } from 'react';
import { MapPin, Camera, Eye, ArrowRight, Check, Image as ImageIcon, Loader2, ChevronDown, MessageCircle, Search, FileText } from 'lucide-react';
import { interactiveCardsAPI, chatAPI } from '../services/api';
import { getImageUrl } from '../config/runtime';
import StreamingContentRenderer from './StreamingContentRenderer';
import CanvasCroppedImage from './CanvasCroppedImage';
import BBoxOverlay from './BBoxOverlay';
// import ThingConfirmationSection from './ThingConfirmationSection';
import StructuredDataForm from './StructuredDataForm';
import PlanUpdateCard from './PlanUpdateCard';
import DecisionCard from './DecisionCard';
// Removed duplicate WebSocket connection - using parent ChatSection's connection

// SVG Pattern Component using external pattern.svg
const GridPattern = memo(({ className = "" }) => (
  <div className={`absolute inset-0 w-full h-full ${className}`}>
    <img
      src="/svg/pattern1.svg"
      alt=""
      className="w-full h-full object-cover opacity-[0.04] dark:opacity-[0.1]"
      style={{ filter: 'brightness(0) saturate(100%)' }}
    />
  </div>
));

const SpotlightThumbnail = memo(({ card, sourceImageUrl, croppedImages, isCollapsed = false }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  const handleLoad = useCallback(() => {
    setTimeout(() => setIsLoaded(true), 100);
  }, []);

  const handleError = useCallback(() => {
    setHasError(true);
    setIsLoaded(false);
  }, []);

  const allBboxes = card?.bounding_boxes || (card?.bounding_box ? [card.bounding_box] : []);
  const primaryBbox = allBboxes[0] || null;
  const useCSSCropping = primaryBbox && sourceImageUrl;
  const croppedImageUrl = !useCSSCropping && croppedImages[card.id]?.length > 0 ? croppedImages[card.id][0] : null;
  const hasValidImage = (useCSSCropping || croppedImageUrl) && !hasError;

  if (isCollapsed) {
    return (
      <div className="relative w-16 min-w-16 min-h-full overflow-hidden">
        {hasValidImage && (
          useCSSCropping ? (
            <div className="absolute inset-0 w-full h-full rounded-l-2xl overflow-hidden">
              <CanvasCroppedImage
                src={getImageUrl(sourceImageUrl)}
                bbox={primaryBbox}
                alt={card.entity || card.title}
                className={`w-full h-full transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
                onLoad={handleLoad}
                onError={handleError}
              />
            </div>
          ) : (
            <img
              src={croppedImageUrl ? getImageUrl(croppedImageUrl) : ''}
              alt={card.entity || card.title}
              className={`absolute inset-0 w-full h-full object-cover rounded-l-2xl transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
              onLoad={handleLoad}
              onError={handleError}
              loading="lazy"
            />
          )
        )}
        <div className={`absolute inset-0 w-full h-full bg-gray-100 dark:bg-gray-700 rounded-l-2xl flex items-center justify-center transition-opacity duration-300 ${hasValidImage && isLoaded ? 'opacity-0' : 'opacity-100'}`}>
          <Search size={20} className="text-gray-600 dark:text-gray-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-12 h-12 sm:w-14 sm:h-14">
      {hasValidImage && (
        useCSSCropping ? (
          <CanvasCroppedImage
            src={getImageUrl(sourceImageUrl)}
            bbox={primaryBbox}
            alt={card.entity || card.title}
            className={`absolute inset-0 w-full h-full rounded-xl transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={handleLoad}
            onError={handleError}
          />
        ) : (
          <img
            src={croppedImageUrl ? getImageUrl(croppedImageUrl) : ''}
            alt={card.entity || card.title}
            className={`absolute inset-0 w-full h-full object-cover rounded-xl transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={handleLoad}
            onError={handleError}
            loading="lazy"
          />
        )
      )}
      <div className={`absolute inset-0 w-full h-full bg-green-200 rounded-xl flex items-center justify-center transition-opacity duration-300 ${hasValidImage && isLoaded ? 'opacity-0' : 'opacity-100'}`}>
          <Search size={24} className="text-gray-600 dark:text-gray-400" />
      </div>
    </div>
  );
});

const SpotlightHero = memo(({ card, sourceImageUrl, croppedImages, isExpanded, openLightbox }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  const handleLoad = useCallback(() => {
    setTimeout(() => setIsLoaded(true), 100);
  }, []);

  const handleError = useCallback(() => {
    setHasError(true);
    setIsLoaded(false);
  }, []);

  const allBboxes = card?.bounding_boxes || (card?.bounding_box ? [card.bounding_box] : []);
  const useCSSCropping = allBboxes[0] && sourceImageUrl;
  const croppedImageUrl = !useCSSCropping && croppedImages[card.id]?.length > 0 ? croppedImages[card.id][0] : null;
  const hasValidImage = (useCSSCropping || croppedImageUrl) && !hasError;

  return (
    <div
      className={`overflow-hidden transition-all duration-300 ${isExpanded ? 'max-h-[520px] opacity-100' : 'max-h-0 opacity-0'}`}
      aria-hidden={!isExpanded}
    >
      <div className="relative w-full">
        {hasValidImage ? (
          useCSSCropping ? (
            <div className="w-full max-h-[400px] overflow-hidden cursor-zoom-in" onClick={(e) => openLightbox(e, card)}>
              <BBoxOverlay
                src={getImageUrl(sourceImageUrl)}
                bboxes={allBboxes}
                alt={card.entity || card.title}
                fillMode="contain"
                className={`w-full h-full transition-all duration-300 ${isExpanded ? 'scale-100' : 'scale-95'} ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
                onLoad={handleLoad}
                onError={handleError}
              />
            </div>
          ) : (
            <img
              src={croppedImageUrl ? getImageUrl(croppedImageUrl) : ''}
              alt={card.entity || card.title}
              className={`w-full max-h-[400px] object-contain rounded-2xl cursor-zoom-in transition-all duration-300 ${isExpanded ? 'scale-100' : 'scale-95'} ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
              onClick={(e) => openLightbox(e, card)}
              onLoad={handleLoad}
              onError={handleError}
              loading="lazy"
            />
          )
        ) : (
          <div className="w-full h-48 bg-gray-100 dark:bg-gray-700 rounded-2xl flex items-center justify-center">
            <Search size={32} className="text-gray-600 dark:text-gray-400" />
          </div>
        )}
      </div>
    </div>
  );
});


/**
 * Interactive action cards that appear below AI messages
 * Unified component for all card types including spotlight cards
 * Supports all card types including spotlight cards with image handling
 */
const InteractiveCards = ({ 
  cards = [], 
  onCardClick, 
  className = '', 
  messageId = null, 
  handledCardIds = [],
  chatIdForStreaming = null,
  registerHandler = null,
  // Spotlight-specific props
  croppedImages = {},
  sourceImageUrl = null,
  assistantMessageId = null,
  onOpenSpotlightViewer = null,
  onLearnMore = null,
  onInteractiveCardInteraction = null,
  // Map integration props
  onLocationCardExpanded = null,
  onLocationCardCollapsed = null,
  userLocation = null, // {lat, lng} from ChatSection
  // Read-only mode
  isReadOnly = false,
  // Messages for finding submitted structured data
  allMessages = [],
  // Objective match statuses (fetched once at ChatSection level to deduplicate API calls)
  objectiveMatchStatuses = {},
  projectId = null,
  // Card states from message metadata (for plan_update cards)
  cardStates = {}
}) => {
  // Using parent ChatSection's WebSocket connection instead of creating duplicate
  // Consolidated state for better performance
  // NOTE: No localStorage - decision state comes from backend via handledCardIds/cardStates props
  const [cardState, setCardState] = useState(() => ({
    recentlyClicked: new Set(),
    matchStatuses: {},
    explainLoading: {},
    clickLoading: {},
    optimisticallyHandled: new Set(),
    instructionPrompts: {},
    instructionImageUrls: {}, // Track instruction image URLs for React re-renders
    expandedCardIndex: null,
    expandedLocationCards: new Set(), // Session-only, not persisted
    selectedDecisionOptions: {} // Restored from backend card_states via useEffect
  }));
  
  // Removed setIsLoadingStatuses - statuses are now fetched at ChatSection level
  // Refs to track each card for scrolling
  const cardRefs = useRef([]);
  // Debounce scroll actions and avoid overlapping scrolls
  const scrollTimerRef = useRef(null);
  const scrollingRef = useRef(false);
  // Debounce card expansion to prevent rapid clicks
  const expansionTimerRef = useRef(null);
  // Track streaming content for learn cards
  const [streamingContent, setStreamingContent] = useState({}); // { [cardId]: string }
  // Track streaming state for learn cards
  const [isStreaming, setIsStreaming] = useState({}); // { [cardId]: boolean }
  // Track loading state for location search
  const [locationSearchLoading, setLocationSearchLoading] = useState({}); // { [cardId]: boolean }
  // Track fetched locations for cards with search_intent
  const [fetchedLocations, setFetchedLocations] = useState({}); // { [cardId]: locations[] }
  // Track error state for location search
  const [locationSearchError, setLocationSearchError] = useState({}); // { [cardId]: string }
  // Restore selected decision options from card_states when cardStates prop changes
  // This ensures persisted selections are restored after page refresh
  useEffect(() => {
    if (!cardStates || typeof cardStates !== 'object') return;
    setCardState(prev => {
      const restored = {};
      for (const [cardId, state] of Object.entries(cardStates)) {
        if (typeof state === 'object' && state.selectedOptionId) {
          restored[cardId] = state.selectedOptionId;
        }
      }
      // Merge with existing state (don't overwrite optimistic updates)
      const updated = { ...prev.selectedDecisionOptions, ...restored };
      // Only update if there are changes
      if (JSON.stringify(updated) !== JSON.stringify(prev.selectedDecisionOptions)) {
        return { ...prev, selectedDecisionOptions: updated };
      }
      return prev;
    });
  }, [cardStates]);

  // BUG 4 FIX: Hydrate learn/spotlight streaming state from persisted card payloads
  useEffect(() => {
    if (!Array.isArray(cards) || cards.length === 0) return;
    setStreamingContent(prev => {
      const next = { ...prev };
      for (const c of cards) {
        if (c && c.id) {
          if (c.type === 'learn' && c.learn_content && !next[c.id]) {
            next[c.id] = c.learn_content;
          }
          if (c.type === 'spotlight' && c.show_more_content && !next[c.id]) {
            next[c.id] = c.show_more_content;
          }
        }
      }
      return next;
    });
    
    // Hydrate instruction image URLs from cards prop
    setCardState(prev => {
      const next = { ...prev };
      let updated = false;
      for (const c of cards) {
        if (c && c.id && c.instruction_image_url && !next.instructionImageUrls[c.id]) {
          if (!next.instructionImageUrls) {
            next.instructionImageUrls = {};
          }
          next.instructionImageUrls[c.id] = c.instruction_image_url;
          updated = true;
        }
      }
      return updated ? next : prev;
    });
    
    // Clear any stale streaming states in read-only mode
    if (isReadOnly) {
      setIsStreaming({});
    }
  }, [cards, isReadOnly]);
  // Unified stream handling for learn cards
  // Track whether location pins are currently shown on the map
  const locationPinsShownRef = useRef(false);
  // Removed "Why?" feature - card title is sufficient, reasoning adds noise without value

  // Unified stream handling for learn cards
  useEffect(() => {
    if (!registerHandler || !chatIdForStreaming || !messageId) return;
    const offChunk = registerHandler('stream_chunk', (data) => {
      const streamTarget = data?.stream_target;
      const streamMsgId = data?.message_id;
      const chunk = data?.chunk || data?.content || '';
      if (!streamTarget || !streamTarget.startsWith('learn_card_')) return;
      if (streamMsgId !== messageId) return;
      const cardId = streamTarget.replace('learn_card_', '');
      setStreamingContent(prev => ({
        ...prev,
        [cardId]: (prev[cardId] || '') + chunk
      }));
    });
    const offComplete = registerHandler('stream_complete', (data) => {
      const streamTarget = data?.stream_target;
      const streamMsgId = data?.message_id;
      if (!streamTarget || !streamTarget.startsWith('learn_card_')) return;
      if (streamMsgId !== messageId) return;
      const cardId = streamTarget.replace('learn_card_', '');
      setIsStreaming(prev => ({ ...prev, [cardId]: false }));
    });
    return () => {
      try { offChunk && offChunk(); } catch (_) {}
      try { offComplete && offComplete(); } catch (_) {}
    };
  }, [registerHandler, chatIdForStreaming, messageId]);

  // NOTE: Removed localStorage persistence for expandedLocationCards and selectedDecisionOptions
  // These are now session-only optimistic states. Backend (via handledCardIds/cardStates) is the source of truth.

  // Track if component is mounted to prevent cleanup on re-renders
  const isMountedRef = useRef(true);
  
  // Ensure pins are hidden if component unmounts while shown
  useEffect(() => {
    return () => {
      // Only call onLocationCardCollapsed if component is actually unmounting
      if (!isMountedRef.current && locationPinsShownRef.current && typeof onLocationCardCollapsed === 'function') {
        onLocationCardCollapsed();
      }
    };
  }, [onLocationCardCollapsed]);
  
  // Mark component as unmounted when it actually unmounts
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Cleanup expansion timer on unmount
  useEffect(() => {
    return () => {
      if (expansionTimerRef.current) {
        clearTimeout(expansionTimerRef.current);
      }
    };
  }, []);

  // Optimized card filtering and sorting - single pass with type-based ordering
  const { objectiveCards, planUpdateCards, decisionCards, expandableCards } = useMemo(() => {
    if (!Array.isArray(cards) || cards.length === 0) {
      return { objectiveCards: [], planUpdateCards: [], decisionCards: [], expandableCards: [] };
    }
    
    const objectiveCards = [];
    const planUpdateCards = [];
    const decisionCards = [];
    const expandableCards = [];
    
    // Separate objective cards from expandable cards
    for (const card of cards) {
      if (card.type === 'objective_match' || card.type === 'multi_objective_match' || card.type === 'objective_match_placeholder') {
        objectiveCards.push(card);
      } else if (card.type === 'plan_update') {
        planUpdateCards.push(card);
      } else if (card.type === 'decision') {
        decisionCards.push(card);
      } else {
        expandableCards.push(card);
      }
    }
    
    // 🎯 Sort expandable cards by type and intent for better visual hierarchy
    // Order: spotlight (visual analysis) → observe (actions) → observe with structured data → location (explore) → learn (education)
    const getCardSortOrder = (card) => {
      const type = card.type || '';
      const intent = card.intent || '';
      
      // Special handling for structured data capture cards (observe with intent)
      if (type === 'observe' && intent === 'capture_structured_data') {
        return 2.5; // Between regular observe (1) and location (2)
      }
      
      const typeOrder = {
        'spotlight': 0,    // What was identified (visual analysis)
        'decision': 1,     // Strategic choices and conflicts 
        'observe': 2,      // Immediate actions (what to do)
        'location': 3,     // Where to go (exploration)
        'learn': 4         // Educational content (learn more)
      };
      
      return typeOrder[type] !== undefined ? typeOrder[type] : 999;
    };
    
    // Create a new sorted array (don't mutate the original)
    // This ensures React detects the change and re-renders correctly
    const sortedExpandableCards = [...expandableCards].sort((a, b) => {
      const aOrder = getCardSortOrder(a);
      const bOrder = getCardSortOrder(b);
      
      // Primary sort: by type/intent order
      if (aOrder !== bOrder) {
        return aOrder - bOrder;
      }
      
      // Secondary sort: maintain original order within same type/intent (stable sort)
      // This preserves any intentional ordering from the backend
      return 0;
    });
    
    return { objectiveCards, planUpdateCards, decisionCards, expandableCards: sortedExpandableCards };
  }, [cards]);

  // Use objective match statuses from parent (fetched once at ChatSection level)
  // This deduplicates API calls when multiple messages have objective match cards
  useEffect(() => {
    // Always update matchStatuses, even if empty (to clear stale data)
    setCardState(prev => ({ ...prev, matchStatuses: objectiveMatchStatuses || {} }));
  }, [objectiveMatchStatuses]);

  const isObjectiveCardDisabled = useCallback((card) => {
    if (!card || !Array.isArray(card.actions)) return false;
    const anyActionWithMatch = card.actions.find(a => a?.matchId);
    if (!anyActionWithMatch) return false;
    const status = cardState.matchStatuses?.[anyActionWithMatch.matchId]?.status;
    return status === 'CONFIRMED' || status === 'DISMISSED';
  }, [cardState.matchStatuses]);

  // Get the actual status of an objective card (CONFIRMED, DISMISSED, or null)
  // Only returns CONFIRMED or DISMISSED - any other status is treated as pending (null)
  const getObjectiveCardStatus = useCallback((card) => {
    if (!card || !Array.isArray(card.actions)) return null;
    const anyActionWithMatch = card.actions.find(a => a?.matchId);
    if (!anyActionWithMatch) return null;
    const status = cardState.matchStatuses?.[anyActionWithMatch.matchId]?.status;
    // Only recognize final states - anything else means the card is still actionable
    if (status === 'CONFIRMED' || status === 'DISMISSED') {
      return status;
    }
    return null;
  }, [cardState.matchStatuses]);

  const handleLearnCardStreaming = useCallback(async (card) => {
    if (!messageId || !card?.id) return;
    
    // Disable streaming in read-only mode
    if (isReadOnly) {
      return;
    }
    
    // console.log('📚 Starting learn card streaming for:', { cardId: card.id, title: card.title, messageId });
    
    setIsStreaming(prev => ({ ...prev, [card.id]: true }));
    setStreamingContent(prev => ({ ...prev, [card.id]: '' }));
    
    try {
      // Use the unified streaming system via chat API
      await chatAPI.streamLearnCard({
        messageId,
        cardId: card.id,
        cardTitle: card.title,
        cardSubtitle: card.subtitle
      });
      
      // Streaming will be handled via WebSocket
      // console.log('📚 Learn card streaming initiated for card:', card.id);
      
    } catch (error) {
      console.error('Error initiating learn card streaming:', error);
      // Fallback content
      const fallbackContent = `Learn more about ${card.title}: ${card.subtitle || 'Detailed information will be provided here.'}`;
      setStreamingContent(prev => ({ ...prev, [card.id]: fallbackContent }));
      setIsStreaming(prev => ({ ...prev, [card.id]: false }));
    }
  }, [isReadOnly, messageId]);


  const openLightbox = useCallback((e, card) => {
    if (e) e.stopPropagation();
    if (typeof onOpenSpotlightViewer === 'function') {
      onOpenSpotlightViewer({ spotlight: card, assistantMessageId, sourceImageUrl });
      return;
    }
    // no-op if no viewer is provided
  }, [onOpenSpotlightViewer, assistantMessageId, sourceImageUrl]);


  // Helper function to trigger mobile panel expansion
  const triggerMobilePanelExpansion = useCallback((cardType, isExpanding = true) => {
    if (typeof onInteractiveCardInteraction === 'function' && isExpanding) {
      onInteractiveCardInteraction(cardType);
    }
  }, [onInteractiveCardInteraction]);

  // Find the nearest scrollable ancestor (or window)
  const getScrollableAncestor = useCallback((element) => {
    if (!element) return window;
    let parent = element.parentElement;
    while (parent && parent !== document.body) {
      try {
        const style = window.getComputedStyle(parent);
        const overflowY = style.overflowY;
        if (overflowY === 'auto' || overflowY === 'scroll') {
          return parent;
        }
      } catch (_) {
        // ignore
      }
      parent = parent.parentElement;
    }
    return window;
  }, []);

  // Smoothly scroll to align card top with chat panel top
  const scrollIntoViewIfNeeded = useCallback((element, options = {}) => {
    if (!element) return;
    const { topOffset = 0, behavior = 'smooth', waitForLayout = false } = options;
    
    const performScroll = () => {
      const container = getScrollableAncestor(element);
      const isWindow = container === window;
      
      // Use getBoundingClientRect for accurate positioning
      const elementRect = element.getBoundingClientRect();
      const containerRect = isWindow ? { top: 0 } : container.getBoundingClientRect();
      
      // Calculate how much we need to scroll to align element top with container top + offset
      const scrollDelta = elementRect.top - containerRect.top - topOffset;
      
      // Only scroll if we need to move significantly
      if (Math.abs(scrollDelta) < 5) return;

      try {
        if (isWindow) {
          window.scrollBy({ top: scrollDelta, behavior });
        } else {
          container.scrollBy({ top: scrollDelta, behavior });
        }
      } catch (_) {
        // fallback without smooth behavior
        if (isWindow) {
          window.scrollBy(0, scrollDelta);
        } else {
          container.scrollTop += scrollDelta;
        }
      }
    };

    if (waitForLayout) {
      // Wait for layout changes to settle (mobile panel height changes, etc.)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          performScroll();
        });
      });
    } else {
      performScroll();
    }
  }, [getScrollableAncestor]);

  const ensureCardVisible = useCallback((cardIndex, waitForLayout = false) => {
    const element = cardRefs.current[cardIndex];
    if (!element) return;
    const prefersReducedMotion = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const topOffset = 16; // Offset to ensure title is visible
    scrollIntoViewIfNeeded(element, { topOffset, behavior: prefersReducedMotion ? 'auto' : 'smooth', waitForLayout });
  }, [scrollIntoViewIfNeeded]);

  const toggleCardExpansion = useCallback((cardIndex) => {
    // Debounce rapid clicks to prevent multiple expansions
    if (expansionTimerRef.current) {
      clearTimeout(expansionTimerRef.current);
    }
    
    expansionTimerRef.current = setTimeout(() => {
      const newExpandedIndex = cardState.expandedCardIndex === cardIndex ? null : cardIndex;
      const card = expandableCards[cardIndex];
      const isExpanding = newExpandedIndex !== null;
      const isCollapsing = !isExpanding && cardState.expandedCardIndex === cardIndex;
    
    // Trigger mobile panel expansion only when expanding cards
    if (isExpanding) {
      triggerMobilePanelExpansion(card.type, true);
    }
    
    // Store scroll position before collapse for smooth UX
    let preCollapseScrollPosition = null;
    let preCollapseCardTop = null;
    if (isCollapsing && cardRefs.current[cardIndex]) {
      const container = getScrollableAncestor(cardRefs.current[cardIndex]);
      const isWindow = container === window;
      preCollapseScrollPosition = isWindow ? window.scrollY : container.scrollTop;
      preCollapseCardTop = cardRefs.current[cardIndex].getBoundingClientRect().top;
    }
    
    setCardState(prev => {
      const newExpandedLocationCards = new Set(prev.expandedLocationCards);
      
      // Track location cards that have been expanded at least once
      if (card && card.type === 'location' && newExpandedIndex !== null) {
        newExpandedLocationCards.add(card.id);
      }
      
      return {
        ...prev,
        expandedCardIndex: newExpandedIndex,
        expandedLocationCards: newExpandedLocationCards
      };
    });
    
    // Handle map pin visibility and location fetching for location cards
    if (card && card.type === 'location') {
      if (newExpandedIndex !== null) {
        // Card expanded - fetch locations if needed
        const hasSearchIntent = card.search_intent && !card.locations;
        const hasFetchedLocations = fetchedLocations[card.id] && Array.isArray(fetchedLocations[card.id]) && fetchedLocations[card.id].length > 0;
        
        if (hasSearchIntent && !hasFetchedLocations && !locationSearchLoading[card.id]) {
          // Fetch locations using search_intent
          setLocationSearchLoading(prev => ({ ...prev, [card.id]: true }));
          // Clear any previous error
          setLocationSearchError(prev => {
            const next = { ...prev };
            delete next[card.id];
            return next;
          });
          
          // Get user location from props (passed from ChatSection)
          const userLocationForSearch = userLocation && typeof userLocation.lat === 'number' && typeof userLocation.lng === 'number'
            ? { lat: userLocation.lat, lng: userLocation.lng }
            : null;
          
          interactiveCardsAPI.searchLocations({
            searchIntent: card.search_intent,
            userLocation: userLocationForSearch
          })
            .then(response => {
              const locations = response.data?.data?.locations || [];
              // Validate locations have required fields and valid coordinates
              const validLocations = locations.filter(loc => {
                if (!loc || !loc.id || !loc.name || !loc.location) return false;
                const lat = loc.location.lat;
                const lng = loc.location.lng;
                return (
                  typeof lat === 'number' && 
                  typeof lng === 'number' &&
                  lat >= -90 && lat <= 90 &&
                  lng >= -180 && lng <= 180
                );
              });
              
              setFetchedLocations(prev => ({ ...prev, [card.id]: validLocations }));
              
              // Show pins for fetched locations
              if (typeof onLocationCardExpanded === 'function' && validLocations.length > 0) {
                onLocationCardExpanded(validLocations);
                locationPinsShownRef.current = true;
              }
            })
            .catch(error => {
              console.error('Failed to fetch locations:', error);
              const errorMessage = error.response?.data?.detail || error.message || 'Failed to search locations';
              setLocationSearchError(prev => ({ ...prev, [card.id]: errorMessage }));
              // Set empty array to show error message
              setFetchedLocations(prev => ({ ...prev, [card.id]: [] }));
            })
            .finally(() => {
              setLocationSearchLoading(prev => {
                const next = { ...prev };
                delete next[card.id];
                return next;
              });
            });
        } else if ((card.locations && Array.isArray(card.locations) && card.locations.length > 0) || (fetchedLocations[card.id] && Array.isArray(fetchedLocations[card.id]) && fetchedLocations[card.id].length > 0)) {
          // Card has locations - show pins
          const locationsToShow = (card.locations && Array.isArray(card.locations) && card.locations.length > 0) 
            ? card.locations 
            : (fetchedLocations[card.id] && Array.isArray(fetchedLocations[card.id]) ? fetchedLocations[card.id] : []);
          if (typeof onLocationCardExpanded === 'function' && locationsToShow.length > 0) {
            onLocationCardExpanded(locationsToShow);
            locationPinsShownRef.current = true;
          }
        }
      } else {
        // Card collapsed - hide pins
        if (typeof onLocationCardCollapsed === 'function') {
          onLocationCardCollapsed();
          locationPinsShownRef.current = false;
        }
      }
    }
    
    // Handle scroll behavior based on expand/collapse
    if (cardRefs.current[cardIndex]) {
      if (scrollTimerRef.current) {
        clearTimeout(scrollTimerRef.current);
      }
      
      if (isExpanding) {
        // Expanding: Only scroll if card header is not already visible
        const isMobile = typeof window !== 'undefined' && window.innerWidth <= 640;
        const waitForLayout = isMobile && typeof onInteractiveCardInteraction === 'function';
        
        const performScrollIfNeeded = () => {
          if (scrollingRef.current) return;
          
          try {
            const element = cardRefs.current[cardIndex];
            if (!element) return;
            
            const container = getScrollableAncestor(element);
            const isWindow = container === window;
            const elementRect = element.getBoundingClientRect();
            const containerRect = isWindow ? { top: 0 } : container.getBoundingClientRect();
            
            // Check if card header is already visible (within top 100px of viewport)
            const headerVisibleThreshold = 100;
            const isHeaderVisible = elementRect.top >= containerRect.top - headerVisibleThreshold && 
                                   elementRect.top <= containerRect.top + headerVisibleThreshold;
            
            // Only scroll if header is not already in a good position
            if (!isHeaderVisible) {
              scrollingRef.current = true;
              ensureCardVisible(cardIndex, waitForLayout);
              setTimeout(() => { scrollingRef.current = false; }, 150);
            }
          } catch (_) {
            // Fallback: ensure card is visible
            scrollingRef.current = true;
            ensureCardVisible(cardIndex, waitForLayout);
            setTimeout(() => { scrollingRef.current = false; }, 150);
          }
        };
        
        if (waitForLayout) {
          scrollTimerRef.current = setTimeout(() => {
            performScrollIfNeeded();
          }, 300);
        } else {
          scrollTimerRef.current = setTimeout(() => {
            performScrollIfNeeded();
          }, 16);
        }
      } else if (isCollapsing && preCollapseScrollPosition !== null && preCollapseCardTop !== null) {
        // Collapsing: Instantly maintain visual continuity by compensating for height change
        const performInstantCollapseScroll = () => {
          if (scrollingRef.current) return;
          scrollingRef.current = true;
          
          try {
            const container = getScrollableAncestor(cardRefs.current[cardIndex]);
            const isWindow = container === window;
            
            // Calculate how much the card's top position changed
            const currentCardTop = cardRefs.current[cardIndex].getBoundingClientRect().top;
            const cardTopDelta = currentCardTop - preCollapseCardTop;
            
            // Calculate the scroll adjustment needed to maintain visual continuity
            // We want the card to appear in the same visual position as before collapse
            const scrollAdjustment = cardTopDelta;
            
            // Only adjust if the change is significant
            if (Math.abs(scrollAdjustment) > 5) {
              // INSTANT adjustment - no animation, no delay
              if (isWindow) {
                window.scrollBy(0, scrollAdjustment);
              } else {
                container.scrollTop += scrollAdjustment;
              }
            }
          } catch (_) {
            // Fallback: just ensure card is visible
            ensureCardVisible(cardIndex, false);
          } finally {
            setTimeout(() => { scrollingRef.current = false; }, 50);
          }
        };
        
        // INSTANT adjustment - no delay, happens immediately after state change
        requestAnimationFrame(() => {
          performInstantCollapseScroll();
        });
      }
    }
    
      // Trigger streaming for learn cards when expanded, but only if no content exists yet
      if (newExpandedIndex !== null) {
        if (card && card.type === 'learn' && !streamingContent[card.id] && !card.learn_content) {
          // In read-only mode, don't start streaming - just show placeholder or existing content
          if (isReadOnly) {
            const placeholderContent = `Learn more about ${card.title}: ${card.subtitle || 'Content not available in read-only mode.'}`;
            setStreamingContent(prev => ({ ...prev, [card.id]: placeholderContent }));
          } else {
            handleLearnCardStreaming(card);
          }
        } else if (card && card.type === 'learn' && card.learn_content && !streamingContent[card.id]) {
          // Load existing content into streaming state for display
          setStreamingContent(prev => ({ ...prev, [card.id]: card.learn_content }));
        } else if (card && card.type === 'spotlight' && card.show_more_content && !streamingContent[card.id]) {
          // Load existing spotlight streaming content into streaming state for display
          setStreamingContent(prev => ({ ...prev, [card.id]: card.show_more_content }));
        }
      }
    }, 50); // 50ms debounce
  }, [isReadOnly, cardState.expandedCardIndex, expandableCards, handleLearnCardStreaming, streamingContent, onLocationCardExpanded, onLocationCardCollapsed, ensureCardVisible, triggerMobilePanelExpansion, onInteractiveCardInteraction, getScrollableAncestor, fetchedLocations, locationSearchLoading, userLocation]);

  const handleCardClick = useCallback((card, cardIndex) => {
    // For observe/learn/spotlight/location cards, toggle expansion instead of triggering action
    if (card.type === 'observe' || card.type === 'learn' || card.type === 'spotlight' || card.type === 'location' || card.intent === 'capture_structured_data') {
      toggleCardExpansion(cardIndex);
      return;
    }

    // For other card types, call the provided callback with the card's action
    if (onCardClick) {
      onCardClick(card.action, card);
    }

    // Show temporary feedback
    setCardState(prev => {
      const newRecentlyClicked = new Set(prev.recentlyClicked);
      newRecentlyClicked.add(card.id);
      return { ...prev, recentlyClicked: newRecentlyClicked };
    });
    
    // Remove feedback after a short delay
    setTimeout(() => {
      setCardState(prev => {
        const newRecentlyClicked = new Set(prev.recentlyClicked);
        newRecentlyClicked.delete(card.id);
        return { ...prev, recentlyClicked: newRecentlyClicked };
      });
    }, 7000);
  }, [onCardClick, toggleCardExpansion]);

  const getCardIcon = (type, intent) => {
    // Use form icon for structured data capture cards
    if (intent === 'capture_structured_data') {
      return <FileText size={16} />;
    }
    
    switch (type) {
      case 'location':
        return <MapPin size={16} />;
      case 'observe':
        return <Camera size={16} />;
      case 'learn':
        return <Eye size={16} />;
      case 'spotlight':
        return <Search size={16} />;
      case 'objective_match':
      case 'objective_match_placeholder':
        return <Check size={16} />;
      default:
        return <ArrowRight size={16} />;
    }
  };

  // Optimization: Memoize submitted form IDs to avoid iterating allMessages in render loop
  const submittedFormIds = useMemo(() => {
    const submitted = new Set();
    if (allMessages && Array.isArray(allMessages)) {
      for (const msg of allMessages) {
        if (msg.role === 'user' && msg.message_metadata?.structured_data?.form_card_id) {
          submitted.add(msg.message_metadata.structured_data.form_card_id);
        }
      }
    }
    return submitted;
  }, [allMessages]);

  // Find submitted photos for observe cards (similar to form submissions)
  const submittedObservePhotos = useMemo(() => {
    const photos = {}; // { cardId: { imageUrl, message } }
    if (allMessages && Array.isArray(allMessages)) {
      for (const msg of allMessages) {
        if (msg.role === 'user' && msg.message_metadata?.selected_action_card && msg.message_metadata?.action_card_id) {
          const cardId = msg.message_metadata.action_card_id;
          const imageUrl = msg.message_metadata?.imageUrl || msg.imageUrl;
          if (imageUrl && msg.message_metadata?.action_card_type === 'observe') {
            // Store the most recent photo for this card
            if (!photos[cardId] || new Date(msg.createdAt || 0) > new Date(photos[cardId].message?.createdAt || 0)) {
              photos[cardId] = { imageUrl, message: msg };
            }
          }
        }
      }
    }
    return photos;
  }, [allMessages]);

  // Mark observe cards as handled when photos are submitted
  useEffect(() => {
    if (!messageId || !cards || !Array.isArray(cards)) return;
    
    for (const card of cards) {
      if (card.type === 'observe' && card.id && submittedObservePhotos[card.id]) {
        const photo = submittedObservePhotos[card.id];
        // Check if card is already marked as handled
        const isAlreadyHandled = handledCardIds && handledCardIds.includes && handledCardIds.includes(card.id);
        if (!isAlreadyHandled) {
          // Mark card as handled with photo URL in card_states
          interactiveCardsAPI.markHandled({ 
            messageId, 
            cardId: card.id, 
            state: { 
              state: 'completed', 
              submittedPhotoUrl: photo.imageUrl 
            } 
          }).catch(() => {});
        }
      }
    }
  }, [messageId, cards, submittedObservePhotos, handledCardIds]);

  // Helper function to determine if a card should show completion checkmark
  const shouldShowCompletionCheckmark = useCallback((card, isHandled) => {
    if (card.type === 'observe') {
      // Show checkmark for completed observe cards (handled OR has submitted photo)
      return isHandled || !!submittedObservePhotos[card.id];
    }
    if (card.type === 'learn') {
      // Show checkmark for learn cards that have generated content
      return streamingContent[card.id] || card.learn_content;
    }
    if (card.type === 'spotlight') {
      // Show checkmark for spotlight cards that have Learn More content
      return streamingContent[card.id] || card.show_more_content;
    }
    if (card.type === 'location') {
      // Show checkmark for location cards that have been expanded at least once
      return cardState.expandedLocationCards.has(card.id);
    }
    if (card.intent === 'capture_structured_data') {
      // Show checkmark for structured data forms that have been submitted
      if (submittedFormIds.has(card.id)) {
        return true;
      }
      // Also check if card is marked as handled
      return isHandled;
    }
    return false;
  }, [streamingContent, cardState.expandedLocationCards, submittedFormIds, submittedObservePhotos]);

  const getActionButtonText = (card) => {
    // Use intent field if available, otherwise fall back to type-based logic
    const intent = card.intent;
    const type = card.type;
    
    if (intent) {
      switch (intent) {
        case 'capture_photo':
          return 'Take Photo';
        case 'show_location':
          return 'Show Location';
        case 'provide_info':
          return 'Get Information';
        case 'confirm_objective':
          return 'Confirm Objective';
        case 'capture_structured_data':
          return 'Fill Form';
        default:
          return card.action || card.title;
      }
    }
    
    // Fallback to type-based logic
    switch (type) {
      case 'observe':
        return 'Take Photo';
      case 'location':
        return 'Show Location';
      case 'learn':
        return 'Learn More';
      case 'spotlight':
        return 'Learn More';
      default:
        return card.action || card.title;
    }
  };

  const formatInstructionPrompt = (prompt) => {
    if (!prompt) return '';
    
    // Split by common panel separators and clean up
    let steps = prompt
      .split(/Panel-\d+:|Panel \d+:|Step \d+:|^\d+\./m)
      .map(step => step.trim())
      .filter(step => step.length > 0);
    
    // If no clear separators found, try to split by periods or semicolons
    if (steps.length <= 1) {
      steps = prompt
        .split(/[.;]\s+/)
        .map(step => step.trim())
        .filter(step => step.length > 0);
    }
    
    // If still no clear steps, return as-is
    if (steps.length <= 1) {
      return <p className="text-[14px] text-gray-600 dark:text-gray-300 leading-relaxed">{prompt}</p>;
    }
    
    // Clean up each step to remove any leading/trailing numbers or punctuation
    const cleanedSteps = steps.map(step => {
      // Remove leading numbers, periods, spaces, and other punctuation
      let cleaned = step.replace(/^[\d\s.\-*+)\]}\s]*/, '').trim();
      // Remove trailing numbers and punctuation (like ".2", ".3", etc.)
      cleaned = cleaned.replace(/[\d\s.\-*+)\]}\s]*$/, '').trim();
      return cleaned;
    }).filter(step => step.length > 0);
    
    // Format as beautifully styled numbered list
    return (
      <ol className="space-y-2.5">
        {cleanedSteps.map((step, index) => (
          <li key={index} className="flex items-start gap-3">
            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-[11px] font-semibold text-gray-500 dark:text-gray-400 mt-0.5">
              {index + 1}
            </span>
            <span className="text-[14px] text-gray-600 dark:text-gray-300 leading-relaxed flex-1">
              {step}
            </span>
          </li>
        ))}
      </ol>
    );
  };

  // Color only the icon backdrop, keep rest neutral
  const getIconBackdropStyle = (type, intent) => {
    // Structured data capture cards get purple/violet color to distinguish from other card types
    if (intent === 'capture_structured_data') {
      return 'bg-purple-100 dark:bg-purple-400/20 text-purple-600 dark:text-purple-400';
    }
    
    switch (type) {
      case 'observe':
        return 'bg-green-100 dark:bg-green-400/20 text-green-400 dark:text-green-500';
      case 'learn':
        return 'bg-blue-100 dark:bg-blue-400/20 text-blue-400 dark:text-blue-500';
      case 'location':
        return 'bg-yellow-100 dark:bg-yellow-400/10 text-yellow-600 dark:text-yellow-500';
      case 'spotlight':
        return 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500';
      case 'objective_match':
      case 'objective_match_placeholder':
        return 'bg-green-500 dark:bg-green-400/20 text-green-400 dark:text-green-500';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500';
    }
  };

  const renderConfidenceBadge = useCallback((confidence) => {
    if (confidence === null || confidence === undefined) return null;
    const val = Number(confidence);
    let label = 'Medium';
    let cls = 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300';
    if (val >= 70) {
      label = 'High';
      cls = 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300';
    } else if (val < 40) {
      label = 'Low';
      cls = 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
    }
    return (
      <span className={`inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-full ${cls}`}>
        Confidence: {label}
      </span>
    );
  }, []);

  // Memoize card groupings
  const cardGroups = useMemo(() => {
    const spotlightCards = expandableCards
      .map((card, index) => ({ card, index }))
      .filter(({ card }) => card.type === 'spotlight');
    const otherCards = expandableCards
      .map((card, index) => ({ card, index }))
      .filter(({ card }) => card.type !== 'spotlight');
    
    return {
      spotlightCards,
      otherCards,
      spotlightCount: spotlightCards.length,
      otherCount: otherCards.length
    };
  }, [expandableCards]);

  if (!cards || cards.length === 0) {
    return null;
  }

  return (
    <div>
      {/* Decision cards section - Critical priority */}
      {decisionCards.length > 0 && (
        <div className="card-base">
          <div className="card-base-header flex items-center gap-2">
            <div className="w-1 h-4 bg-indigo-500 dark:bg-indigo-400 rounded-full"></div>
            <span className="text-[11px] uppercase tracking-[0.08em] text-indigo-600 dark:text-indigo-400 font-bold">
              Decision Needed
            </span>
          </div>
          {decisionCards.map((card, index) => {
              // Check if already handled from backend (source of truth) or optimistic session state
              const isHandledFromBackend = handledCardIds && handledCardIds.includes && handledCardIds.includes(card.id);
              const isHandledOptimistic = cardState.optimisticallyHandled.has(card.id);
              const selectedOptionId = cardState.selectedDecisionOptions[card.id] || null;
              const isAlreadyHandled = isHandledFromBackend || isHandledOptimistic || selectedOptionId !== null;
              
              return (
                <DecisionCard
                  key={`decision_${card.id}_${index}`}
                  card={card}
                  onOptionSelect={(option) => {
                    // Optimistically track selection (session only, not persisted)
                    setCardState(prev => ({
                      ...prev,
                      selectedDecisionOptions: {
                        ...prev.selectedDecisionOptions,
                        [card.id]: option.id
                      },
                      optimisticallyHandled: new Set([...prev.optimisticallyHandled, card.id])
                    }));
                    
                    // Send prefixed message to chat
                    if (onCardClick) {
                      const message = option.action_message || option.label;
                      const prefixedMessage = `Decision: ${message}`;
                      onCardClick(prefixedMessage, {
                        ...card,
                        decision_data: {
                          optionId: option.id,
                          label: option.label
                        }
                      });
                    }
                    
                    // Mark card as handled on backend (persisted, source of truth)
                    // Store selected option ID in card_states for persistence
                    if (messageId) {
                      interactiveCardsAPI.markHandled({ 
                        messageId, 
                        cardId: card.id, 
                        state: { 
                          state: 'completed', 
                          selectedOptionId: option.id 
                        } 
                      }).catch(() => {});
                    }
                  }}
                  disabled={isReadOnly || isAlreadyHandled}
                  selectedOptionId={selectedOptionId}
                />
              );
           })}
        </div>
      )}

      {/* Render spotlight cards */}
      {cardGroups.spotlightCards.length > 0 && (
        <div className="card-base">
          {/* Spotlight header */}
          <div className="card-base-header flex items-center gap-2 pt-2">
            <div className="w-1 h-4 bg-gray-400 dark:bg-gray-500 rounded-full"></div>
            <span className="text-[11px] uppercase tracking-[0.08em] text-gray-500 dark:text-gray-400 font-semibold">
              {cardGroups.spotlightCount === 1 ? '1 element identified' : `${cardGroups.spotlightCount} elements identified`}
            </span>
          </div>
          
          <div className="card-base-group">
          {cardGroups.spotlightCards.map(({ card, index }) => {
            const isRecentlyClicked = cardState.recentlyClicked.has(card.id);
            const hasSubmittedPhoto = !!submittedObservePhotos[card.id];
            const isHandled = (handledCardIds && handledCardIds.includes && handledCardIds.includes(card.id)) || cardState.optimisticallyHandled.has(card.id) || hasSubmittedPhoto;
            const isExpanded = cardState.expandedCardIndex === index;
            const isExpandable = card.type === 'observe' || card.type === 'learn' || card.type === 'spotlight' || card.type === 'location';
            const showCompletionCheckmark = shouldShowCompletionCheckmark(card, isHandled);
            const submittedPhoto = submittedObservePhotos[card.id];
            
            return (
              <React.Fragment key={`fragment_${card.id}_${index}`}>
            
            <div
              key={`${card.id}_${messageId || 'no-message'}_${index}`}
            ref={(el) => { cardRefs.current[index] = el; }}
            role="button"
            tabIndex={0}
            aria-label={`${card.title}${card.subtitle ? ` - ${card.subtitle}` : ''}`}
            aria-disabled={cardState.clickLoading[card.id]}
            aria-expanded={isExpanded}
            style={{ scrollMarginTop: '0px' }}
            onClick={async (e) => {
              // Don't toggle if clicking inside a form element (but allow header clicks)
              // Check if the click target is inside a form element
              const formElement = e.target.closest('form');
              if (formElement) {
                return; // Don't toggle if clicking inside form
              }
              
              // For expandable cards, toggle expansion
              if (isExpandable) {
                toggleCardExpansion(index);
                return;
              }
              
              // For other cards, handle normally (but disable in read-only mode)
              if (isReadOnly) {
                return;
              }
              if (cardState.clickLoading[card.id]) return;
              
              // Trigger mobile panel expansion for non-expandable cards
              triggerMobilePanelExpansion(card.type, true);
              
              // Set loading state
              setCardState(prev => ({ ...prev, clickLoading: { ...prev.clickLoading, [card.id]: true } }));
              
              try {
                // Persist handled state to backend for regular action cards
                if (messageId && card?.id) {
                  try {
                    setCardState(prev => {
                      const newOptimisticallyHandled = new Set(prev.optimisticallyHandled);
                      newOptimisticallyHandled.add(card.id);
                      return { ...prev, optimisticallyHandled: newOptimisticallyHandled };
                    });
                    await interactiveCardsAPI.markHandled({ messageId, cardId: card.id });
                  } catch (_) {
                    // ignore network errors; UI still proceeds
                  }
                }
                handleCardClick(card, index);
              } finally {
                // Clear loading state after a short delay to show feedback
                setTimeout(() => {
                  setCardState(prev => ({ ...prev, clickLoading: { ...prev.clickLoading, [card.id]: false } }));
                }, 500);
              }
            }}
            onKeyDown={(e) => {
              // Don't handle keyboard events if user is typing in an input/textarea/select
              const target = e.target;
              if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
                return;
              }
              
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                if (isExpandable) {
                  toggleCardExpansion(index);
                } else if (!cardState.clickLoading[card.id] && !isReadOnly) {
                  // Trigger the same click handler
                  e.target.click();
                }
              }
            }}
            className={`
              dark:bg-gray-800 bg-white text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700
              rounded-2xl cursor-pointer select-none transition-shadow duration-200
              ${isExpanded ? 'p-0 shadow-md ring-1 ring-gray-900/5 dark:ring-white/10' : 'p-0 flex min-h-[64px] shadow-sm hover:shadow-md'}
            `}
            title={card.action}
          >
            {isExpanded ? (
              // Expanded layout - harmonized to match collapsed state visually
              <>
                <div className="flex min-h-[64px] border-b border-gray-200 dark:border-gray-600/30">
                  {/* Full-height icon/image on the left - matching collapsed state */}
                  {card.type === 'spotlight' ? <SpotlightThumbnail card={card} sourceImageUrl={sourceImageUrl} croppedImages={croppedImages} isCollapsed={true} /> : (
                    <div className="relative w-16 min-h-full overflow-hidden flex-shrink-0">
                      <div className={`absolute inset-0 w-full h-full ${getIconBackdropStyle(card.type, card.intent)} rounded-tl-2xl flex items-center justify-center overflow-hidden ring-1 ring-inset ring-gray-200/50 dark:ring-gray-600/30`}>
                        <GridPattern />
                        <div className="relative z-10">
                          {cardState.clickLoading[card.id] ? (
                            <Loader2 size={28} className="text-gray-600 dark:text-gray-400 animate-spin" />
                          ) : (!isHandled && isRecentlyClicked) ? (
                            <Check size={28} className="text-blue-600 dark:text-blue-400" />
                          ) : (
                            <div className="scale-[1.75]">{getCardIcon(card.type, card.intent)}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Content on the right with padding - matching collapsed state */}
                  <div className="flex-1 py-4 p-4 sm:px-5 flex items-center min-w-0">
                    <div className="flex items-center justify-between w-full gap-3">
                      <div className="flex-1 min-w-0">
                        <h3 className={`text-[15px] sm:text-base font-semibold leading-snug text-gray-900 dark:text-gray-100 ${card.type === 'location' ? 'tracking-wide' : ''}`}>
                          {card.title}
                        </h3>
                      </div>
                      
                      {/* Completion checkmark and chevron */}
                      <div className="flex-shrink-0 flex items-center gap-2">
                        {showCompletionCheckmark && (
                          <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
                            <Check size={12} className="text-white" strokeWidth={3} />
                          </div>
                        )}
                        {isExpandable && (
                          <div className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}>
                            <ChevronDown size={18} className="text-gray-400 dark:text-gray-500" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Expanding hero image for spotlight cards */}
                {card.type === 'spotlight' && <SpotlightHero card={card} sourceImageUrl={sourceImageUrl} croppedImages={croppedImages} isExpanded={isExpanded} openLightbox={openLightbox} />}
                
                {/* Expanded Content for spotlight cards */}
                {isExpanded && card.type === 'spotlight' && card.description && (
                  <div className="px-4 sm:px-5 pb-5 pt-5">
                    <div className="p-0">
                      <p className="text-[15px] text-gray-700 dark:text-gray-300 leading-relaxed">
                        {card.description}
                      </p>
                      
                      {/* Learn More button - hide in read-only mode */}
                      {!streamingContent[card.id] && !isReadOnly && (
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            // Disable all interactions in read-only mode
                            if (isReadOnly) {
                              return;
                            }
                            if (!messageId || !card?.id) return;
                            
                            // Trigger mobile panel expansion
                            triggerMobilePanelExpansion(card.type, true);
                            
                            // Start streaming for this spotlight card
                            setIsStreaming(prev => ({ ...prev, [card.id]: true }));
                            setStreamingContent(prev => ({ ...prev, [card.id]: '' }));
                            
                            try {
                              // Use the unified streaming system via chat API (same as learn cards)
                              await chatAPI.streamLearnCard({
                                messageId,
                                cardId: card.id,
                                cardTitle: card.title,
                                cardSubtitle: card.subtitle
                              });
                              
                              // console.log('🔍 Spotlight card streaming initiated for card:', card.id);
                            } catch (error) {
                              console.error('Error initiating spotlight card streaming:', error);
                              // Fallback content
                              const fallbackContent = `Learn more about ${card.title}: ${card.subtitle || 'Detailed information will be provided here.'}`;
                              setStreamingContent(prev => ({ ...prev, [card.id]: fallbackContent }));
                              setIsStreaming(prev => ({ ...prev, [card.id]: false }));
                            }
                          }}
                          className="
                            inline-flex items-center gap-2 px-4 py-3 mt-4
                            bg-gray-900 hover:bg-gray-800 dark:bg-white dark:hover:bg-gray-100
                            text-white dark:text-gray-900 text-sm font-semibold rounded-xl 
                            shadow-sm hover:shadow-md min-h-[44px] transition-all
                          "
                        >
                          <MessageCircle size={16} />
                          Learn More
                        </button>
                      )}
                      
                      {/* Streaming content for spotlight cards */}
                      {(isStreaming[card.id] || streamingContent[card.id]) && (
                        <div className="mt-5 pt-4 border-t border-gray-200 dark:border-gray-700">
                          <div className="prose prose-sm max-w-none dark:prose-invert prose-headings:font-semibold prose-headings:text-gray-900 dark:prose-headings:text-gray-100 prose-p:text-gray-600 dark:prose-p:text-gray-300 prose-p:leading-relaxed prose-li:text-gray-600 dark:prose-li:text-gray-300">
                            {isStreaming[card.id] ? (
                              <StreamingContentRenderer 
                                content={streamingContent[card.id] || ''} 
                                isStreaming={true}
                                className="text-gray-600 dark:text-gray-300"
                              />
                            ) : streamingContent[card.id] ? (
                              <StreamingContentRenderer 
                                content={streamingContent[card.id]} 
                                isStreaming={false}
                                className="text-gray-600 dark:text-gray-300"
                              />
                            ) : null}
                          </div>
                        </div>
                      )}
                      
                      
                    </div>
                  </div>
                )}
                {/* Thing Confirmation Section - hide in read-only mode */}
                {/* {card.detection_id && !isReadOnly && (
                  <ThingConfirmationSection
                    key={`${card.id}-${card.detection_id}`}
                    detectionId={card.detection_id}
                    cardId={card.id}
                    onConfirmationChange={(action, thingId, data) => {
                      console.log('Thing confirmation changed:', action, thingId, data);
                      if (action === 'confirmed') {
                        console.log('Successfully confirmed match with thing:', thingId);
                      } else if (action === 'rejected') {
                        console.log('Rejected match with thing:', thingId);
                      }
                    }}
                    className="mt-4"
                  />
                )} */}
                {/* Expanded content for location cards */}
                {isExpanded && card.type === 'location' && (
                  <div className="p-4 sm:p-5 bg-gray-50/50 dark:bg-gray-800/50 rounded-b-2xl">
                    {locationSearchLoading[card.id] ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                        <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Searching for locations...</span>
                      </div>
                    ) : locationSearchError[card.id] ? (
                      <div className="text-center py-8">
                        <p className="text-sm text-red-600 dark:text-red-400 mb-2">Failed to search locations</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{locationSearchError[card.id]}</p>
                      </div>
                    ) : ((card.locations && Array.isArray(card.locations) && card.locations.length > 0) || (fetchedLocations[card.id] && Array.isArray(fetchedLocations[card.id]) && fetchedLocations[card.id].length > 0)) ? (
                      <div>
                        {(card.locations || fetchedLocations[card.id] || []).map((location, locationIndex) => (
                        <div
                          key={location.id || locationIndex}
                          className="flex items-start gap-3 p-3 bg-white dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600/30 hover:border-blue-300 dark:hover:border-blue-500 cursor-pointer hover:shadow-md transition-all"
                          onClick={async (e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            
                            // Disable all interactions in read-only mode
                            if (isReadOnly) {
                              return;
                            }
                            
                            // Trigger mobile panel expansion
                            triggerMobilePanelExpansion(card.type, true);
                            
                            // Collapse the location card
                            setCardState(prev => ({ ...prev, expandedCardIndex: null }));
                            // Ensure map pins are hidden when selecting a location item
                            if (typeof onLocationCardCollapsed === 'function') {
                              onLocationCardCollapsed();
                            }
                            locationPinsShownRef.current = false;
                            
                            // Handle location selection - convert to grouped format with single location
                            const selectedLocationCard = {
                              id: location.id,
                              type: 'location',
                              title: location.name,
                              subtitle: location.description || '',
                              locations: [location]
                            };
                            
                            // Call the card click handler with the selected location
                            if (onCardClick) {
                              onCardClick(null, selectedLocationCard);
                            }
                          }}
                        >
                          <div className="flex-shrink-0 mt-1">
                            <div className="w-7 h-7 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg flex items-center justify-center">
                              <MapPin size={16} className="text-yellow-600 dark:text-yellow-400" />
                            </div>
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-tight">
                                  {location.name}
                                </h4>
                                {location.description && (
                                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 leading-relaxed">
                                    {location.description}
                                  </p>
                                )}
                              </div>
                              
                              {location.distance && (
                                <div className="flex-shrink-0">
                                  <span className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-full font-medium">
                                    {location.distance}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">
                        No locations found
                      </div>
                    )}
                  </div>
                )}
                
                {/* Generated image - hide when form is submitted */}
                {isExpanded && 
                 (card.instruction_image_url || cardState.instructionImageUrls[card.id]) && 
                 !(card.intent === 'capture_structured_data' && submittedFormIds.has(card.id)) && (
                  <div className="w-full overflow-hidden">
                    <img
                      src={getImageUrl(cardState.instructionImageUrls[card.id] || card.instruction_image_url)}
                      alt="Instruction"
                      className="w-full h-auto object-cover bg-gray-50 dark:bg-gray-800"
                      loading="lazy"
                    />
                  </div>
                )}

                {/* Expanded content for other card types */}
                {isExpanded && card.type !== 'spotlight' && card.type !== 'location' && (
                  <div className="p-4 sm:p-5 space-y-5">
                  
                  {/* Step-by-step instructions for observe cards - hide when form is submitted */}
                  {card.type === 'observe' && 
                   !(card.intent === 'capture_structured_data' && submittedFormIds.has(card.id)) &&
                   (cardState.instructionPrompts[card.id] || card.instruction_prompt) && (
                    <div>
                      <div className="flex items-center justify-between gap-3">
                        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 tracking-wide uppercase">
                          How to do this
                        </h4>
                        {/* Generate Image button - inline with header, mobile-friendly */}
                        {!(card.instruction_image_url || cardState.instructionImageUrls[card.id]) && !isReadOnly && (
                          <button
                            type="button"
                            disabled={cardState.explainLoading[card.id]}
                            onClick={async (e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              if (isReadOnly) return;
                              if (!messageId || !card?.id || cardState.explainLoading[card.id]) return;
                              triggerMobilePanelExpansion(card.type);
                              setCardState(prev => ({ ...prev, explainLoading: { ...prev.explainLoading, [card.id]: true } }));
                              try {
                                const res = await interactiveCardsAPI.explain({ messageId, cardId: card.id });
                                const url = res?.data?.data?.instruction_image_url || null;
                                const prompt = res?.data?.data?.instruction_prompt || '';
                                if (url) {
                                  setCardState(prev => ({ ...prev, instructionImageUrls: { ...prev.instructionImageUrls, [card.id]: url } }));
                                }
                                if (prompt) {
                                  setCardState(prev => ({ ...prev, instructionPrompts: { ...prev.instructionPrompts, [card.id]: prompt } }));
                                }
                              } catch (_) {
                                // swallow errors
                              } finally {
                                setCardState(prev => ({ ...prev, explainLoading: { ...prev.explainLoading, [card.id]: false } }));
                              }
                            }}
                            className="inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] text-xs font-semibold rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-700/50 hover:bg-blue-100 dark:hover:bg-blue-900/50 active:scale-[0.98] transition-all disabled:opacity-50 flex-shrink-0"
                          >
                            {cardState.explainLoading[card.id] ? <Loader2 size={14} className="animate-spin" /> : <ImageIcon size={14} />}
                            <span>Visualize</span>
                          </button>
                        )}
                      </div>
                      <div>
                        {formatInstructionPrompt(cardState.instructionPrompts[card.id] || card.instruction_prompt)}
                      </div>
                    </div>
                  )}
                  
                  {/* Generate Image button for learn cards - show when no instructions */}
                  {card.type === 'learn' && !(card.instruction_image_url || cardState.instructionImageUrls[card.id]) && !isReadOnly && (
                    <div className="flex justify-end">
                      <button
                        type="button"
                        disabled={cardState.explainLoading[card.id]}
                        onClick={async (e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          if (isReadOnly) return;
                          if (!messageId || !card?.id || cardState.explainLoading[card.id]) return;
                          triggerMobilePanelExpansion(card.type);
                          setCardState(prev => ({ ...prev, explainLoading: { ...prev.explainLoading, [card.id]: true } }));
                          try {
                            const res = await interactiveCardsAPI.explain({ messageId, cardId: card.id });
                            const url = res?.data?.data?.instruction_image_url || null;
                            const prompt = res?.data?.data?.instruction_prompt || '';
                            if (url) {
                              setCardState(prev => ({ ...prev, instructionImageUrls: { ...prev.instructionImageUrls, [card.id]: url } }));
                            }
                            if (prompt) {
                              setCardState(prev => ({ ...prev, instructionPrompts: { ...prev.instructionPrompts, [card.id]: prompt } }));
                            }
                          } catch (_) {
                            // swallow errors
                          } finally {
                            setCardState(prev => ({ ...prev, explainLoading: { ...prev.explainLoading, [card.id]: false } }));
                          }
                        }}
                        className="inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] text-xs font-semibold rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-700/50 hover:bg-blue-100 dark:hover:bg-blue-900/50 active:scale-[0.98] transition-all disabled:opacity-50"
                      >
                        {cardState.explainLoading[card.id] ? <Loader2 size={14} className="animate-spin" /> : <ImageIcon size={14} />}
                        <span>Generate Image</span>
                      </button>
                    </div>
                  )}
                  
                  {/* Streaming content for learn cards */}
                  {card.type === 'learn' && (
                    <div className="prose prose-sm max-w-none dark:prose-invert prose-headings:text-gray-900 dark:prose-headings:text-gray-100 prose-p:text-gray-600 dark:prose-p:text-gray-300 prose-li:text-gray-600 dark:prose-li:text-gray-300 prose-p:text-[14px] prose-li:text-[14px] prose-p:leading-relaxed prose-li:leading-relaxed">
                      {isStreaming[card.id] && !streamingContent[card.id] ? (
                        <div className="flex items-center gap-2 text-gray-400 dark:text-gray-500 py-2">
                          <Loader2 size={14} className="animate-spin" />
                          <span className="text-sm">{isReadOnly ? 'Content not available' : 'Generating...'}</span>
                        </div>
                      ) : isStreaming[card.id] ? (
                        <StreamingContentRenderer 
                          content={streamingContent[card.id] || ''} 
                          isStreaming={true}
                          className="text-gray-600 dark:text-gray-300"
                        />
                      ) : streamingContent[card.id] ? (
                        <StreamingContentRenderer 
                          content={streamingContent[card.id]} 
                          isStreaming={false}
                          className="text-gray-600 dark:text-gray-300"
                        />
                      ) : (
                        <div className="flex items-center gap-2 text-gray-400 dark:text-gray-500 py-2">
                          <Loader2 size={14} className="animate-spin" />
                          <span className="text-sm">{isReadOnly ? 'Content not available' : 'Generating...'}</span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Structured data form for capture_structured_data intent */}
                  {card.intent === 'capture_structured_data' && card.form_fields && card.form_fields.length > 0 && !isReadOnly && (() => {
                    // Find submitted structured data for this card from user messages
                    let submittedData = null;
                    let submittedMessage = null;
                    if (allMessages && Array.isArray(allMessages) && card.id) {
                      // Find the most recent user message with structured_data matching this card
                      const userMessages = allMessages
                        .filter(m => m.role === 'user' && m.message_metadata?.structured_data)
                        .reverse(); // Most recent first
                      
                      for (const msg of userMessages) {
                        const structuredData = msg.message_metadata.structured_data;
                        if (structuredData?.form_card_id === card.id) {
                          submittedData = structuredData;
                          submittedMessage = msg;
                          break;
                        }
                      }
                    }
                    
                    const isSubmitted = !!submittedData;
                    
                    // Format submission date/time
                    const formatSubmissionTime = (dateString) => {
                      if (!dateString) return '';
                      try {
                        const date = new Date(dateString);
                        const now = new Date();
                        const diffMs = now - date;
                        const diffMins = Math.floor(diffMs / 60000);
                        const diffHours = Math.floor(diffMs / 3600000);
                        const diffDays = Math.floor(diffMs / 86400000);
                        
                        if (diffMins < 1) return 'just now';
                        if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
                        if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
                        if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
                        
                        // For older submissions, show date and time
                        return date.toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: 'numeric',
                          year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
                        }) + ' at ' + date.toLocaleTimeString('en-US', { 
                          hour: 'numeric', 
                          minute: '2-digit',
                          hour12: true 
                        });
                      } catch {
                        return '';
                      }
                    };
                    
                    return (
                      <div className="space-y-4" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                        {/* Hide description when submitted - not needed anymore */}
                        {card.description && !isSubmitted && (
                          <p className="text-[14px] text-gray-600 dark:text-gray-400 leading-relaxed">
                            {card.description}
                          </p>
                        )}
                        {isSubmitted && (
                          <div className="flex items-center gap-3 px-4 py-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50 rounded-xl">
                            <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                              <Check size={14} className="text-white" strokeWidth={3} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-[14px] font-semibold text-green-800 dark:text-green-300">
                                Form submitted
                              </div>
                              <div className="text-[12px] text-green-700 dark:text-green-400 mt-0.5">
                                {formatSubmissionTime(submittedData.submitted_at || submittedMessage?.createdAt)}
                              </div>
                            </div>
                          </div>
                        )}
                        <StructuredDataForm
                          formFields={card.form_fields}
                          cardTitle={card.title}
                          cardId={card.id}
                          initialValues={submittedData?.values || null}
                          initialFields={submittedData?.fields || null}
                          disabled={isSubmitted}
                          onSubmit={async (structuredData) => {
                            try {
                              // Optimistically mark card as handled in local state for immediate UI feedback
                              if (card?.id) {
                                setCardState(prev => {
                                  const newOptimisticallyHandled = new Set(prev.optimisticallyHandled);
                                  newOptimisticallyHandled.add(card.id);
                                  return { ...prev, optimisticallyHandled: newOptimisticallyHandled };
                                });
                              }
                              
                              // Mark card as handled on backend
                              if (messageId && card?.id) {
                                try {
                                  await interactiveCardsAPI.markHandled({ messageId, cardId: card.id });
                                } catch (_) {
                                  // ignore network errors
                                }
                              }
                              // Submit structured data via onCardClick
                              if (onCardClick) {
                                onCardClick(card.title || 'Form submitted', {
                                  ...card,
                                  structured_data: structuredData
                                });
                                // Collapse card after successful submission
                                setCardState(prev => ({ ...prev, expandedCardIndex: null }));
                              }
                            } catch (error) {
                              console.error('Form submission error:', error);
                              // Error is already handled by StructuredDataForm
                            }
                          }}
                          onCancel={() => {
                            // Collapse card on cancel
                            setCardState(prev => ({ ...prev, expandedCardIndex: null }));
                          }}
                        />
                      </div>
                    );
                  })()}
                  
                  {/* Show submitted photo if available */}
                  {card.type === 'observe' && submittedPhoto && (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 px-4 py-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50 rounded-xl">
                        <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                          <Check size={14} className="text-white" strokeWidth={3} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[14px] font-semibold text-green-800 dark:text-green-300">
                            Photo submitted
                          </div>
                        </div>
                      </div>
                      <div className="w-full overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
                        <img
                          src={getImageUrl(submittedPhoto.imageUrl)}
                          alt="Submitted observation"
                          className="w-full h-auto object-cover"
                          loading="lazy"
                        />
                      </div>
                    </div>
                  )}

                  {/* Action button for observe cards - hide if photo already submitted, in read-only mode, or when form is shown */}
                  {card.type === 'observe' && !isReadOnly && card.intent !== 'capture_structured_data' && !submittedPhoto && (
                    <button
                      type="button"
                      onClick={async (e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        if (isReadOnly) return;
                        if (cardState.clickLoading[card.id]) return;
                        triggerMobilePanelExpansion(card.type);
                        setCardState(prev => ({ ...prev, clickLoading: { ...prev.clickLoading, [card.id]: true } }));
                        try {
                          if (messageId && card?.id) {
                            try {
                              setCardState(prev => {
                                const newOptimisticallyHandled = new Set(prev.optimisticallyHandled);
                                newOptimisticallyHandled.add(card.id);
                                return { ...prev, optimisticallyHandled: newOptimisticallyHandled };
                              });
                              await interactiveCardsAPI.markHandled({ messageId, cardId: card.id });
                            } catch (_) {}
                          }
                          if (card.type === 'observe' && onCardClick) {
                            onCardClick(card.action, card);
                            setCardState(prev => ({ ...prev, expandedCardIndex: null }));
                          } else {
                            handleCardClick(card);
                          }
                        } finally {
                          setTimeout(() => {
                            setCardState(prev => ({ ...prev, clickLoading: { ...prev.clickLoading, [card.id]: false } }));
                          }, 500);
                        }
                      }}
                      disabled={cardState.clickLoading[card.id]}
                      className={`
                        w-full py-3.5 text-[14px] font-semibold rounded-xl transition-all duration-200
                        ${cardState.clickLoading[card.id]
                          ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                          : 'bg-green-500 text-white hover:bg-green-600 active:scale-[0.98] shadow-sm hover:shadow-md'
                        }
                      `}
                    >
                      {cardState.clickLoading[card.id] ? (
                        <span className="flex items-center justify-center gap-2">
                          <Loader2 size={16} className="animate-spin" />
                          Processing...
                        </span>
                      ) : (
                        getActionButtonText(card)
                      )}
                    </button>
                  )}
                </div>
                )}
              </>
            ) : (
              // Collapsed layout
              <>
                {/* Full-height icon/image on the left - tighter width for better proportion */}
                {card.type === 'spotlight' ? <SpotlightThumbnail card={card} sourceImageUrl={sourceImageUrl} croppedImages={croppedImages} isCollapsed={true} /> : (
                  <div className="relative w-16 min-h-full overflow-hidden flex-shrink-0">
                  <div className={`absolute inset-0 w-full h-full ${getIconBackdropStyle(card.type, card.intent)} rounded-l-2xl flex items-center justify-center overflow-hidden`}>
                    <GridPattern />
                    <div className="relative z-10">
                      {cardState.clickLoading[card.id] ? (
                        <Loader2 size={22} className="text-gray-600 dark:text-gray-400 animate-spin" />
                      ) : (!isHandled && isRecentlyClicked) ? (
                        <Check size={22} className="text-blue-600 dark:text-blue-400" />
                      ) : (
                        <div className="scale-[1.35]">{getCardIcon(card.type, card.intent)}</div>
                      )}
                    </div>
                  </div>
                </div>
                )}
                
                {/* Content on the right with padding */}
                <div className="flex-1 py-4 p-4 sm:px-5 flex items-center min-w-0">
                  <div className="flex items-center justify-between w-full gap-3">
                    <div className="flex-1 min-w-0">
                      {/* Title only in collapsed state - subtitle would be redundant noise */}
                      <h3 className={`text-[15px] sm:text-base font-semibold leading-snug text-gray-900 dark:text-gray-100 ${card.type === 'location' ? 'tracking-wide' : ''}`}>
                        {card.title}
                      </h3>
                    </div>
                    
                    {/* Completion checkmark and chevron */}
                    <div className="flex-shrink-0 flex items-center gap-2">
                      {showCompletionCheckmark && (
                        <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
                          <Check size={12} className="text-white" strokeWidth={3} />
                        </div>
                      )}
                      {isExpandable && (
                        <div className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}>
                          <ChevronDown size={18} className="text-gray-400 dark:text-gray-500" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
          </React.Fragment>
            );
          })}
          </div>
        </div>
      )}

      {/* Render other cards */}
      {cardGroups.otherCards.length > 0 && (
        <div className="card-base">
          {/* Other cards header */}
          <div className="card-base-header flex items-center gap-2 pt-2">
            <div className="w-1 h-4 bg-gray-400 dark:bg-gray-500 rounded-full"></div>
            <span className="text-[11px] uppercase tracking-[0.08em] text-gray-500 dark:text-gray-400 font-semibold">
              {cardGroups.otherCount === 1 ? 'Suggested action' : 'Suggested actions'}
            </span>
          </div>
          
          <div className="card-base-group">
          {cardGroups.otherCards.map(({ card, index }) => {
            const isRecentlyClicked = cardState.recentlyClicked.has(card.id);
            const hasSubmittedPhoto = !!submittedObservePhotos[card.id];
            const isHandled = (handledCardIds && handledCardIds.includes && handledCardIds.includes(card.id)) || cardState.optimisticallyHandled.has(card.id) || hasSubmittedPhoto;
            const isExpanded = cardState.expandedCardIndex === index;
            const isExpandable = card.type === 'observe' || card.type === 'learn' || card.type === 'spotlight' || card.type === 'location';
            const showCompletionCheckmark = shouldShowCompletionCheckmark(card, isHandled);
            const submittedPhoto = submittedObservePhotos[card.id];
            
            return (
              <React.Fragment key={`fragment_${card.id}_${index}`}>
            
            <div
              key={`${card.id}_${messageId || 'no-message'}_${index}`}
            ref={(el) => { cardRefs.current[index] = el; }}
            role="button"
            tabIndex={0}
            aria-label={`${card.title}${card.subtitle ? ` - ${card.subtitle}` : ''}`}
            aria-disabled={cardState.clickLoading[card.id]}
            aria-expanded={isExpanded}
            style={{ scrollMarginTop: '0px' }}
            onClick={async (e) => {
              // Don't toggle if clicking inside a form element (but allow header clicks)
              // Check if the click target is inside a form element
              const formElement = e.target.closest('form');
              if (formElement) {
                return; // Don't toggle if clicking inside form
              }
              
              // For expandable cards, toggle expansion
              if (isExpandable) {
                toggleCardExpansion(index);
                return;
              }
              
              // For other cards, handle normally (but disable in read-only mode)
              if (isReadOnly) {
                return;
              }
              if (cardState.clickLoading[card.id]) return;
              
              // Trigger mobile panel expansion for non-expandable cards
              triggerMobilePanelExpansion(card.type, true);
              
              // Set loading state
              setCardState(prev => ({ ...prev, clickLoading: { ...prev.clickLoading, [card.id]: true } }));
              
              try {
                // Persist handled state to backend for regular action cards
                if (messageId && card?.id) {
                  try {
                    setCardState(prev => {
                      const newOptimisticallyHandled = new Set(prev.optimisticallyHandled);
                      newOptimisticallyHandled.add(card.id);
                      return { ...prev, optimisticallyHandled: newOptimisticallyHandled };
                    });
                    await interactiveCardsAPI.markHandled({ messageId, cardId: card.id });
                  } catch (_) {
                    // ignore network errors; UI still proceeds
                  }
                }
                handleCardClick(card, index);
              } finally {
                // Clear loading state after a short delay to show feedback
                setTimeout(() => {
                  setCardState(prev => ({ ...prev, clickLoading: { ...prev.clickLoading, [card.id]: false } }));
                }, 500);
              }
            }}
            onKeyDown={(e) => {
              // Don't handle keyboard events if user is typing in an input/textarea/select
              const target = e.target;
              if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
                return;
              }
              
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                if (isExpandable) {
                  toggleCardExpansion(index);
                } else if (!cardState.clickLoading[card.id] && !isReadOnly) {
                  // Trigger the same click handler
                  e.target.click();
                }
              }
            }}
            className={`
              dark:bg-gray-800 bg-white text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700
              rounded-2xl cursor-pointer select-none transition-shadow duration-200
              ${isExpanded ? 'p-0 shadow-md ring-1 ring-gray-900/5 dark:ring-white/10' : 'p-0 flex min-h-[64px] shadow-sm hover:shadow-md'}
            `}
            title={card.action}
          >
            {isExpanded ? (
              // Expanded layout - harmonized to match collapsed state visually
              <>
                <div className="flex min-h-[64px] border-b border-gray-200 dark:border-gray-600/30">
                  {/* Full-height icon/image on the left - matching collapsed state */}
                  {card.type === 'spotlight' ? <SpotlightThumbnail card={card} sourceImageUrl={sourceImageUrl} croppedImages={croppedImages} isCollapsed={true} /> : (
                    <div className="relative w-16 min-h-full overflow-hidden flex-shrink-0">
                      <div className={`absolute inset-0 w-full h-full ${getIconBackdropStyle(card.type, card.intent)} rounded-tl-2xl flex items-center justify-center overflow-hidden ring-1 ring-inset ring-gray-200/50 dark:ring-gray-600/30`}>
                        <GridPattern />
                        <div className="relative z-10">
                          {cardState.clickLoading[card.id] ? (
                            <Loader2 size={28} className="text-gray-600 dark:text-gray-400 animate-spin" />
                          ) : (!isHandled && isRecentlyClicked) ? (
                            <Check size={28} className="text-blue-600 dark:text-blue-400" />
                          ) : (
                            <div className="scale-[1.75]">{getCardIcon(card.type, card.intent)}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Content on the right with padding - matching collapsed state */}
                  <div className="flex-1 py-4 p-4 sm:px-5 flex items-center min-w-0">
                    <div className="flex items-center justify-between w-full gap-3">
                      <div className="flex-1 min-w-0">
                        <h3 className={`text-[15px] sm:text-base font-semibold leading-snug text-gray-900 dark:text-gray-100 ${card.type === 'location' ? 'tracking-wide' : ''}`}>
                          {card.title}
                        </h3>
                      </div>
                      
                      {/* Completion checkmark and chevron */}
                      <div className="flex-shrink-0 flex items-center gap-2">
                        {showCompletionCheckmark && (
                          <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
                            <Check size={12} className="text-white" strokeWidth={3} />
                          </div>
                        )}
                        {isExpandable && (
                          <div className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}>
                            <ChevronDown size={18} className="text-gray-400 dark:text-gray-500" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Expanding hero image for spotlight cards */}
                {card.type === 'spotlight' && <SpotlightHero card={card} sourceImageUrl={sourceImageUrl} croppedImages={croppedImages} isExpanded={isExpanded} openLightbox={openLightbox} />}
                
                {/* Expanded Content for spotlight cards */}
                {isExpanded && card.type === 'spotlight' && card.description && (
                  <div className="px-4 sm:px-5 pb-5 pt-5">
                    <div className="p-0">
                      <p className="text-[15px] text-gray-700 dark:text-gray-300 leading-relaxed">
                        {card.description}
                      </p>
                      
                      {/* Learn More button - hide in read-only mode */}
                      {!streamingContent[card.id] && !isReadOnly && (
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            // Disable all interactions in read-only mode
                            if (isReadOnly) {
                              return;
                            }
                            if (!messageId || !card?.id) return;
                            
                            // Trigger mobile panel expansion
                            triggerMobilePanelExpansion(card.type, true);
                            
                            // Start streaming for this spotlight card
                            setIsStreaming(prev => ({ ...prev, [card.id]: true }));
                            setStreamingContent(prev => ({ ...prev, [card.id]: '' }));
                            
                            try {
                              // Use the unified streaming system via chat API (same as learn cards)
                              await chatAPI.streamLearnCard({
                                messageId,
                                cardId: card.id,
                                cardTitle: card.title,
                                cardSubtitle: card.subtitle
                              });
                              
                              // console.log('🔍 Spotlight card streaming initiated for card:', card.id);
                            } catch (error) {
                              console.error('Error initiating spotlight card streaming:', error);
                              // Fallback content
                              const fallbackContent = `Learn more about ${card.title}: ${card.subtitle || 'Detailed information will be provided here.'}`;
                              setStreamingContent(prev => ({ ...prev, [card.id]: fallbackContent }));
                              setIsStreaming(prev => ({ ...prev, [card.id]: false }));
                            }
                          }}
                          className="
                            inline-flex items-center gap-2 px-4 py-3 mt-4
                            bg-gray-900 hover:bg-gray-800 dark:bg-white dark:hover:bg-gray-100
                            text-white dark:text-gray-900 text-sm font-semibold rounded-xl 
                            shadow-sm hover:shadow-md min-h-[44px] transition-all
                          "
                        >
                          <MessageCircle size={16} />
                          Learn More
                        </button>
                      )}
                      
                      {/* Streaming content for spotlight cards */}
                      {(isStreaming[card.id] || streamingContent[card.id]) && (
                        <div className="mt-5 pt-4 border-t border-gray-200 dark:border-gray-700">
                          <div className="prose prose-sm max-w-none dark:prose-invert prose-headings:font-semibold prose-headings:text-gray-900 dark:prose-headings:text-gray-100 prose-p:text-gray-600 dark:prose-p:text-gray-300 prose-p:leading-relaxed prose-li:text-gray-600 dark:prose-li:text-gray-300">
                            {isStreaming[card.id] ? (
                              <StreamingContentRenderer 
                                content={streamingContent[card.id] || ''} 
                                isStreaming={true}
                                className="text-gray-600 dark:text-gray-300"
                              />
                            ) : streamingContent[card.id] ? (
                              <StreamingContentRenderer 
                                content={streamingContent[card.id]} 
                                isStreaming={false}
                                className="text-gray-600 dark:text-gray-300"
                              />
                            ) : null}
                          </div>
                        </div>
                      )}
                      
                      
                    </div>
                  </div>
                )}
                {/* Thing Confirmation Section - hide in read-only mode */}
                {/* {card.detection_id && !isReadOnly && (
                  <ThingConfirmationSection
                    key={`${card.id}-${card.detection_id}`}
                    detectionId={card.detection_id}
                    cardId={card.id}
                    onConfirmationChange={(action, thingId, data) => {
                      console.log('Thing confirmation changed:', action, thingId, data);
                      if (action === 'confirmed') {
                        console.log('Successfully confirmed match with thing:', thingId);
                      } else if (action === 'rejected') {
                        console.log('Rejected match with thing:', thingId);
                      }
                    }}
                    className="mt-4"
                  />
                )} */}
                {/* Expanded content for location cards */}
                {isExpanded && card.type === 'location' && (
                  <div className="p-4 sm:p-5 bg-gray-50/50 dark:bg-gray-800/50 rounded-b-2xl">
                    {locationSearchLoading[card.id] ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                        <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Searching for locations...</span>
                      </div>
                    ) : locationSearchError[card.id] ? (
                      <div className="text-center py-8">
                        <p className="text-sm text-red-600 dark:text-red-400 mb-2">Failed to search locations</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{locationSearchError[card.id]}</p>
                      </div>
                    ) : ((card.locations && Array.isArray(card.locations) && card.locations.length > 0) || (fetchedLocations[card.id] && Array.isArray(fetchedLocations[card.id]) && fetchedLocations[card.id].length > 0)) ? (
                      <div>
                        {(card.locations || fetchedLocations[card.id] || []).map((location, locationIndex) => (
                        <div
                          key={location.id || locationIndex}
                          className="flex items-start gap-3 p-3 bg-white dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600/30 hover:border-blue-300 dark:hover:border-blue-500 cursor-pointer hover:shadow-md transition-all"
                          onClick={async (e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            
                            // Disable all interactions in read-only mode
                            if (isReadOnly) {
                              return;
                            }
                            
                            // Trigger mobile panel expansion
                            triggerMobilePanelExpansion(card.type, true);
                            
                            // Collapse the location card
                            setCardState(prev => ({ ...prev, expandedCardIndex: null }));
                            // Ensure map pins are hidden when selecting a location item
                            if (typeof onLocationCardCollapsed === 'function') {
                              onLocationCardCollapsed();
                            }
                            locationPinsShownRef.current = false;
                            
                            // Handle location selection - convert to grouped format with single location
                            const selectedLocationCard = {
                              id: location.id,
                              type: 'location',
                              title: location.name,
                              subtitle: location.description || '',
                              locations: [location]
                            };
                            
                            // Call the card click handler with the selected location
                            if (onCardClick) {
                              onCardClick(null, selectedLocationCard);
                            }
                          }}
                        >
                          <div className="flex-shrink-0 mt-1">
                            <div className="w-7 h-7 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg flex items-center justify-center">
                              <MapPin size={16} className="text-yellow-600 dark:text-yellow-400" />
                            </div>
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-tight">
                                  {location.name}
                                </h4>
                                {location.description && (
                                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 leading-relaxed">
                                    {location.description}
                                  </p>
                                )}
                              </div>
                              
                              {location.distance && (
                                <div className="flex-shrink-0">
                                  <span className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-full font-medium">
                                    {location.distance}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">
                        No locations found
                      </div>
                    )}
                  </div>
                )}
                
                {/* Generated image - hide when form is submitted */}
                {isExpanded && 
                 (card.instruction_image_url || cardState.instructionImageUrls[card.id]) && 
                 !(card.intent === 'capture_structured_data' && submittedFormIds.has(card.id)) && (
                  <div className="w-full overflow-hidden">
                    <img
                      src={getImageUrl(cardState.instructionImageUrls[card.id] || card.instruction_image_url)}
                      alt="Instruction"
                      className="w-full h-auto object-cover bg-gray-50 dark:bg-gray-800"
                      loading="lazy"
                    />
                  </div>
                )}

                {/* Expanded content for other card types */}
                {isExpanded && card.type !== 'spotlight' && card.type !== 'location' && (
                  <div className="p-4 sm:p-5 space-y-5">
                  
                  {/* Step-by-step instructions for observe cards - hide when form is submitted */}
                  {card.type === 'observe' && 
                   !(card.intent === 'capture_structured_data' && submittedFormIds.has(card.id)) &&
                   (cardState.instructionPrompts[card.id] || card.instruction_prompt) && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between gap-3">
                        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 tracking-wide uppercase">
                          How to do this
                        </h4>
                        {/* Generate Image button - inline with header, mobile-friendly */}
                        {!(card.instruction_image_url || cardState.instructionImageUrls[card.id]) && !isReadOnly && (
                          <button
                            type="button"
                            disabled={cardState.explainLoading[card.id]}
                            onClick={async (e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              if (isReadOnly) return;
                              if (!messageId || !card?.id || cardState.explainLoading[card.id]) return;
                              triggerMobilePanelExpansion(card.type);
                              setCardState(prev => ({ ...prev, explainLoading: { ...prev.explainLoading, [card.id]: true } }));
                              try {
                                const res = await interactiveCardsAPI.explain({ messageId, cardId: card.id });
                                const url = res?.data?.data?.instruction_image_url || null;
                                const prompt = res?.data?.data?.instruction_prompt || '';
                                if (url) {
                                  setCardState(prev => ({ ...prev, instructionImageUrls: { ...prev.instructionImageUrls, [card.id]: url } }));
                                }
                                if (prompt) {
                                  setCardState(prev => ({ ...prev, instructionPrompts: { ...prev.instructionPrompts, [card.id]: prompt } }));
                                }
                              } catch (_) {
                                // swallow errors
                              } finally {
                                setCardState(prev => ({ ...prev, explainLoading: { ...prev.explainLoading, [card.id]: false } }));
                              }
                            }}
                            className="inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] text-xs font-semibold rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-700/50 hover:bg-blue-100 dark:hover:bg-blue-900/50 active:scale-[0.98] transition-all disabled:opacity-50 flex-shrink-0"
                          >
                            {cardState.explainLoading[card.id] ? <Loader2 size={14} className="animate-spin" /> : <ImageIcon size={14} />}
                            <span>Visualize</span>
                          </button>
                        )}
                      </div>
                      <div>
                        {formatInstructionPrompt(cardState.instructionPrompts[card.id] || card.instruction_prompt)}
                      </div>
                    </div>
                  )}
                  
                  {/* Generate Image button for learn cards - show when no instructions */}
                  {card.type === 'learn' && !(card.instruction_image_url || cardState.instructionImageUrls[card.id]) && !isReadOnly && (
                    <div className="flex justify-end">
                      <button
                        type="button"
                        disabled={cardState.explainLoading[card.id]}
                        onClick={async (e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          if (isReadOnly) return;
                          if (!messageId || !card?.id || cardState.explainLoading[card.id]) return;
                          triggerMobilePanelExpansion(card.type);
                          setCardState(prev => ({ ...prev, explainLoading: { ...prev.explainLoading, [card.id]: true } }));
                          try {
                            const res = await interactiveCardsAPI.explain({ messageId, cardId: card.id });
                            const url = res?.data?.data?.instruction_image_url || null;
                            const prompt = res?.data?.data?.instruction_prompt || '';
                            if (url) {
                              setCardState(prev => ({ ...prev, instructionImageUrls: { ...prev.instructionImageUrls, [card.id]: url } }));
                            }
                            if (prompt) {
                              setCardState(prev => ({ ...prev, instructionPrompts: { ...prev.instructionPrompts, [card.id]: prompt } }));
                            }
                          } catch (_) {
                            // swallow errors
                          } finally {
                            setCardState(prev => ({ ...prev, explainLoading: { ...prev.explainLoading, [card.id]: false } }));
                          }
                        }}
                        className="inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] text-xs font-semibold rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-700/50 hover:bg-blue-100 dark:hover:bg-blue-900/50 active:scale-[0.98] transition-all disabled:opacity-50"
                      >
                        {cardState.explainLoading[card.id] ? <Loader2 size={14} className="animate-spin" /> : <ImageIcon size={14} />}
                        <span>Generate Image</span>
                      </button>
                    </div>
                  )}
                  
                  {/* Streaming content for learn cards */}
                  {card.type === 'learn' && (
                    <div className="prose prose-sm max-w-none dark:prose-invert prose-headings:text-gray-900 dark:prose-headings:text-gray-100 prose-p:text-gray-600 dark:prose-p:text-gray-300 prose-li:text-gray-600 dark:prose-li:text-gray-300 prose-p:text-[14px] prose-li:text-[14px] prose-p:leading-relaxed prose-li:leading-relaxed">
                      {isStreaming[card.id] && !streamingContent[card.id] ? (
                        <div className="flex items-center gap-2 text-gray-400 dark:text-gray-500 py-2">
                          <Loader2 size={14} className="animate-spin" />
                          <span className="text-sm">{isReadOnly ? 'Content not available' : 'Generating...'}</span>
                        </div>
                      ) : isStreaming[card.id] ? (
                        <StreamingContentRenderer 
                          content={streamingContent[card.id] || ''} 
                          isStreaming={true}
                          className="text-gray-600 dark:text-gray-300"
                        />
                      ) : streamingContent[card.id] ? (
                        <StreamingContentRenderer 
                          content={streamingContent[card.id]} 
                          isStreaming={false}
                          className="text-gray-600 dark:text-gray-300"
                        />
                      ) : (
                        <div className="flex items-center gap-2 text-gray-400 dark:text-gray-500 py-2">
                          <Loader2 size={14} className="animate-spin" />
                          <span className="text-sm">{isReadOnly ? 'Content not available' : 'Generating...'}</span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Structured data form for capture_structured_data intent */}
                  {card.intent === 'capture_structured_data' && card.form_fields && card.form_fields.length > 0 && !isReadOnly && (() => {
                    // Find submitted structured data for this card from user messages
                    let submittedData = null;
                    let submittedMessage = null;
                    if (allMessages && Array.isArray(allMessages) && card.id) {
                      // Find the most recent user message with structured_data matching this card
                      const userMessages = allMessages
                        .filter(m => m.role === 'user' && m.message_metadata?.structured_data)
                        .reverse(); // Most recent first
                      
                      for (const msg of userMessages) {
                        const structuredData = msg.message_metadata.structured_data;
                        if (structuredData?.form_card_id === card.id) {
                          submittedData = structuredData;
                          submittedMessage = msg;
                          break;
                        }
                      }
                    }
                    
                    const isSubmitted = !!submittedData;
                    
                    // Format submission date/time
                    const formatSubmissionTime = (dateString) => {
                      if (!dateString) return '';
                      try {
                        const date = new Date(dateString);
                        const now = new Date();
                        const diffMs = now - date;
                        const diffMins = Math.floor(diffMs / 60000);
                        const diffHours = Math.floor(diffMs / 3600000);
                        const diffDays = Math.floor(diffMs / 86400000);
                        
                        if (diffMins < 1) return 'just now';
                        if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
                        if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
                        if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
                        
                        // For older submissions, show date and time
                        return date.toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: 'numeric',
                          year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
                        }) + ' at ' + date.toLocaleTimeString('en-US', { 
                          hour: 'numeric', 
                          minute: '2-digit',
                          hour12: true 
                        });
                      } catch {
                        return '';
                      }
                    };
                    
                    return (
                      <div className="space-y-4" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                        {/* Hide description when submitted - not needed anymore */}
                        {card.description && !isSubmitted && (
                          <p className="text-[14px] text-gray-600 dark:text-gray-400 leading-relaxed">
                            {card.description}
                          </p>
                        )}
                        {isSubmitted && (
                          <div className="flex items-center gap-3 px-4 py-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50 rounded-xl">
                            <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                              <Check size={14} className="text-white" strokeWidth={3} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-[14px] font-semibold text-green-800 dark:text-green-300">
                                Form submitted
                              </div>
                              <div className="text-[12px] text-green-700 dark:text-green-400 mt-0.5">
                                {formatSubmissionTime(submittedData.submitted_at || submittedMessage?.createdAt)}
                              </div>
                            </div>
                          </div>
                        )}
                        <StructuredDataForm
                          formFields={card.form_fields}
                          cardTitle={card.title}
                          cardId={card.id}
                          initialValues={submittedData?.values || null}
                          initialFields={submittedData?.fields || null}
                          disabled={isSubmitted}
                          onSubmit={async (structuredData) => {
                            try {
                              // Optimistically mark card as handled in local state for immediate UI feedback
                              if (card?.id) {
                                setCardState(prev => {
                                  const newOptimisticallyHandled = new Set(prev.optimisticallyHandled);
                                  newOptimisticallyHandled.add(card.id);
                                  return { ...prev, optimisticallyHandled: newOptimisticallyHandled };
                                });
                              }
                              
                              // Mark card as handled on backend
                              if (messageId && card?.id) {
                                try {
                                  await interactiveCardsAPI.markHandled({ messageId, cardId: card.id });
                                } catch (_) {
                                  // ignore network errors
                                }
                              }
                              // Submit structured data via onCardClick
                              if (onCardClick) {
                                onCardClick(card.title || 'Form submitted', {
                                  ...card,
                                  structured_data: structuredData
                                });
                                // Collapse card after successful submission
                                setCardState(prev => ({ ...prev, expandedCardIndex: null }));
                              }
                            } catch (error) {
                              console.error('Form submission error:', error);
                              // Error is already handled by StructuredDataForm
                            }
                          }}
                          onCancel={() => {
                            // Collapse card on cancel
                            setCardState(prev => ({ ...prev, expandedCardIndex: null }));
                          }}
                        />
                      </div>
                    );
                  })()}
                  
                  {/* Show submitted photo if available */}
                  {card.type === 'observe' && submittedPhoto && (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 px-4 py-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50 rounded-xl">
                        <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                          <Check size={14} className="text-white" strokeWidth={3} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[14px] font-semibold text-green-800 dark:text-green-300">
                            Photo submitted
                          </div>
                        </div>
                      </div>
                      <div className="w-full overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
                        <img
                          src={getImageUrl(submittedPhoto.imageUrl)}
                          alt="Submitted observation"
                          className="w-full h-auto object-cover"
                          loading="lazy"
                        />
                      </div>
                    </div>
                  )}

                  {/* Action button for observe cards - hide if photo already submitted, in read-only mode, or when form is shown */}
                  {card.type === 'observe' && !isReadOnly && card.intent !== 'capture_structured_data' && !submittedPhoto && (
                    <button
                      type="button"
                      onClick={async (e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        if (isReadOnly) return;
                        if (cardState.clickLoading[card.id]) return;
                        triggerMobilePanelExpansion(card.type);
                        setCardState(prev => ({ ...prev, clickLoading: { ...prev.clickLoading, [card.id]: true } }));
                        try {
                          if (messageId && card?.id) {
                            try {
                              setCardState(prev => {
                                const newOptimisticallyHandled = new Set(prev.optimisticallyHandled);
                                newOptimisticallyHandled.add(card.id);
                                return { ...prev, optimisticallyHandled: newOptimisticallyHandled };
                              });
                              await interactiveCardsAPI.markHandled({ messageId, cardId: card.id });
                            } catch (_) {}
                          }
                          if (card.type === 'observe' && onCardClick) {
                            onCardClick(card.action, card);
                            setCardState(prev => ({ ...prev, expandedCardIndex: null }));
                          } else {
                            handleCardClick(card);
                          }
                        } finally {
                          setTimeout(() => {
                            setCardState(prev => ({ ...prev, clickLoading: { ...prev.clickLoading, [card.id]: false } }));
                          }, 500);
                        }
                      }}
                      disabled={cardState.clickLoading[card.id]}
                      className={`
                        w-full py-3.5 text-[14px] font-semibold rounded-xl transition-all duration-200
                        ${cardState.clickLoading[card.id]
                          ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                          : 'bg-green-500 text-white hover:bg-green-600 active:scale-[0.98] shadow-sm hover:shadow-md'
                        }
                      `}
                    >
                      {cardState.clickLoading[card.id] ? (
                        <span className="flex items-center justify-center gap-2">
                          <Loader2 size={16} className="animate-spin" />
                          Processing...
                        </span>
                      ) : (
                        getActionButtonText(card)
                      )}
                    </button>
                  )}
                </div>
                )}
              </>
            ) : (
              // Collapsed layout
              <>
                {/* Full-height icon/image on the left - tighter width for better proportion */}
                {card.type === 'spotlight' ? <SpotlightThumbnail card={card} sourceImageUrl={sourceImageUrl} croppedImages={croppedImages} isCollapsed={true} /> : (
                  <div className="relative w-16 min-h-full overflow-hidden flex-shrink-0">
                  <div className={`absolute inset-0 w-full h-full ${getIconBackdropStyle(card.type, card.intent)} rounded-l-2xl flex items-center justify-center overflow-hidden`}>
                    <GridPattern />
                    <div className="relative z-10">
                      {cardState.clickLoading[card.id] ? (
                        <Loader2 size={22} className="text-gray-600 dark:text-gray-400 animate-spin" />
                      ) : (!isHandled && isRecentlyClicked) ? (
                        <Check size={22} className="text-blue-600 dark:text-blue-400" />
                      ) : (
                        <div className="scale-[1.35]">{getCardIcon(card.type, card.intent)}</div>
                      )}
                    </div>
                  </div>
                </div>
                )}
                
                {/* Content on the right with padding */}
                <div className="flex-1 py-4 p-4 sm:px-5 flex items-center min-w-0">
                  <div className="flex items-center justify-between w-full gap-3">
                    <div className="flex-1 min-w-0">
                      {/* Title only in collapsed state - subtitle would be redundant noise */}
                      <h3 className={`text-[15px] sm:text-base font-semibold leading-snug text-gray-900 dark:text-gray-100 ${card.type === 'location' ? 'tracking-wide' : ''}`}>
                        {card.title}
                      </h3>
                    </div>
                    
                    {/* Completion checkmark and chevron */}
                    <div className="flex-shrink-0 flex items-center gap-2">
                      {showCompletionCheckmark && (
                        <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
                          <Check size={12} className="text-white" strokeWidth={3} />
                        </div>
                      )}
                      {isExpandable && (
                        <div className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}>
                          <ChevronDown size={18} className="text-gray-400 dark:text-gray-500" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
          </React.Fragment>
            );
          })}
          </div>
        </div>
      )}

      {/* Plan updates section */}
      {planUpdateCards.length > 0 && (
        <div className="card-base">
          <div className="card-base-header flex items-center gap-2">
            <div className="w-1 h-4 bg-blue-500 dark:bg-blue-400 rounded-full"></div>
            <span className="text-[11px] uppercase tracking-[0.08em] text-blue-600 dark:text-blue-400 font-bold">
              Plan Update
            </span>
          </div>
          {planUpdateCards.map((card, index) => (
            <PlanUpdateCard
              key={`plan_update_${card.id}_${index}`}
              card={card}
              projectId={projectId}
              messageId={messageId}
              handledCardIds={handledCardIds}
              cardStates={cardStates}
            />
          ))}
        </div>
      )}

      {/* Objective updates section at the end */}
      {objectiveCards.length > 0 && (
        <div className="card-base">
          <div className="card-base-header flex items-center gap-2">
            <div className="w-1 h-4 bg-emerald-500 dark:bg-emerald-400 rounded-full"></div>
            <span className="text-[11px] uppercase tracking-[0.08em] text-emerald-600 dark:text-emerald-400 font-bold">
              Objective Progress
            </span>
          </div>
          {objectiveCards.map((card, index) => {
            // Helper to extract objective name and evidence from card data
            // Card structure: title = "Confirm objective match: [Evidence]", subtitle = "Matches '[Objective]'"
            const extractMatchInfo = (matchCard) => {
              const evidence = matchCard.title?.replace('Confirm objective match: ', '') || matchCard.title;
              // Extract objective from subtitle like "Matches 'Document Key Features'"
              const objectiveMatch = matchCard.subtitle?.match(/Matches ['"](.+)['"]/);
              const objective = objectiveMatch ? objectiveMatch[1] : matchCard.subtitle;
              return { evidence, objective };
            };
            
            if (card.type === 'objective_match_placeholder') {
              return (
                <div
                  key={`${card.id}_${index}`}
                  className="flex items-center gap-3 p-4 border border-dashed border-emerald-200 dark:border-emerald-800 rounded-2xl bg-emerald-50/40 dark:bg-emerald-900/10"
                >
                  <Loader2 className="text-emerald-500 animate-spin" size={20} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-200">
                      {card.title || 'Analyzing your evidence…'}
                    </p>
                    <p className="text-xs text-emerald-700/80 dark:text-emerald-300/80 mt-0.5">
                      {card.subtitle || 'We’re double-checking your objectives before showing suggestions.'}
                    </p>
                  </div>
                </div>
              );
            }
            
            // Handle Multi-Match Card
            if (card.type === 'multi_objective_match') {
              return (
                <div 
                  key={`${card.id}_${index}`} 
                  className="relative overflow-hidden bg-white dark:bg-gray-800 rounded-2xl border border-emerald-100 dark:border-emerald-900/50 shadow-lg shadow-emerald-100/50 dark:shadow-none ring-1 ring-emerald-50 dark:ring-emerald-900/20"
                >
                  {/* Decorative background pattern */}
                  <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-emerald-50/50 to-teal-50/50 dark:from-emerald-900/10 dark:to-teal-900/10 rounded-bl-full -mr-16 -mt-16 pointer-events-none" />
                  
                  {/* Header */}
                  <div className="relative px-5 py-4 border-b border-emerald-100/80 dark:border-emerald-900/30">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-gradient-to-br from-emerald-100 to-emerald-50 dark:from-emerald-900/50 dark:to-emerald-800/30 rounded-xl text-emerald-600 dark:text-emerald-400 shadow-sm ring-1 ring-emerald-200/50 dark:ring-emerald-700/30">
                        <Check size={18} strokeWidth={2.5} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-bold text-gray-900 dark:text-gray-50 leading-tight">
                          {card.title || 'Objectives Matched'}
                        </h3>
                        {card.subtitle && (
                          <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400 leading-snug">
                            {card.subtitle}
                          </p>
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        <span className="inline-flex items-center px-2.5 py-1 text-xs font-bold tracking-wide text-emerald-700 dark:text-emerald-300 bg-emerald-100 dark:bg-emerald-900/40 rounded-full ring-1 ring-emerald-200/50 dark:ring-emerald-700/30">
                          {card.matches?.length || 0} match{(card.matches?.length || 0) !== 1 ? 'es' : ''}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Matches list */}
                  <div className="relative divide-y divide-emerald-50 dark:divide-emerald-900/20">
                    {card.matches && card.matches.map((matchCard, matchIndex) => {
                      const isRecentlyClicked = cardState.recentlyClicked.has(matchCard.id);
                      const isDisabled = isObjectiveCardDisabled(matchCard);
                      const matchStatus = getObjectiveCardStatus(matchCard);
                      const { evidence, objective } = extractMatchInfo(matchCard);
                      
                      return (
                        <div 
                          key={matchCard.id} 
                          className={`
                            relative p-4 transition-all duration-200
                            ${matchStatus === 'CONFIRMED'
                              ? 'bg-emerald-50/50 dark:bg-emerald-900/10'
                              : matchStatus === 'DISMISSED'
                                ? 'bg-gray-50/50 dark:bg-gray-800/30'
                                : 'hover:bg-emerald-50/30 dark:hover:bg-emerald-900/10'
                            }
                          `}
                        >
                          {/* Success overlay for recently clicked */}
                          {isRecentlyClicked && (
                            <div className="absolute inset-0 bg-emerald-500/5 backdrop-blur-[2px] flex items-center justify-center z-10 animate-fade-in">
                              <div className="bg-emerald-500 text-white px-4 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 shadow-lg shadow-emerald-500/25">
                                <Check size={14} strokeWidth={3} />
                                CONFIRMED
                              </div>
                            </div>
                          )}
                          
                          <div className="flex items-start gap-4">
                            {/* Match index indicator - changes based on status */}
                            <div className="flex-shrink-0 mt-0.5">
                              <span className={`
                                inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold
                                ${matchStatus === 'CONFIRMED'
                                  ? 'bg-emerald-500 text-white'
                                  : matchStatus === 'DISMISSED'
                                    ? 'bg-gray-400 dark:bg-gray-600 text-white'
                                    : 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400'
                                }
                              `}>
                                {matchStatus === 'CONFIRMED' ? (
                                  <Check size={14} strokeWidth={3} />
                                ) : matchStatus === 'DISMISSED' ? (
                                  <span className="text-[10px]">—</span>
                                ) : (
                                  matchIndex + 1
                                )}
                              </span>
                            </div>
                            
                            {/* Content - Clear hierarchy: Objective first, then evidence */}
                            <div className="flex-1 min-w-0">
                              {/* Top row: Objective + Status/Confidence (desktop) + Action */}
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <h4 className={`text-[15px] font-semibold leading-snug ${matchStatus ? 'text-gray-700 dark:text-gray-300' : 'text-gray-900 dark:text-gray-100'}`}>
                                    {objective}
                                  </h4>
                                </div>
                                
                                {/* Right side: Status badge or Confidence + Action button */}
                                <div className="flex-shrink-0 flex items-center gap-3">
                                  {/* Status badge (when actioned) or Confidence badge (when not) */}
                                  <span className="hidden sm:inline-flex">
                                    {matchStatus === 'CONFIRMED' ? (
                                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-bold text-emerald-700 dark:text-emerald-300 bg-emerald-100 dark:bg-emerald-900/40 rounded-full">
                                        <Check size={12} strokeWidth={3} />
                                        Confirmed
                                      </span>
                                    ) : matchStatus === 'DISMISSED' ? (
                                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-bold text-gray-600 dark:text-gray-400 bg-gray-200 dark:bg-gray-700 rounded-full">
                                        Dismissed
                                      </span>
                                    ) : matchCard.confidence !== undefined ? (
                                      renderConfidenceBadge(matchCard.confidence)
                                    ) : null}
                                  </span>
                                  
                                  {/* Actions - only show when not actioned */}
                                  {!isReadOnly && !matchStatus && Array.isArray(matchCard.actions) && matchCard.actions.length > 0 && (
                                    <>
                                      {matchCard.actions.filter(a => a.type === 'confirm_match').map(act => (
                                        <button
                                          key={act.id}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (isReadOnly || isDisabled) return;
                                            
                                            // Optimistically mark as confirmed
                                            setCardState(prev => ({
                                              ...prev,
                                              matchStatuses: {
                                                ...prev.matchStatuses,
                                                [act.matchId]: { ...(prev.matchStatuses?.[act.matchId] || {}), id: act.matchId, status: 'CONFIRMED' }
                                              }
                                            }));
                                            
                                            onCardClick && onCardClick(act, { ...matchCard, action: act });
                                          }}
                                          className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 text-white text-sm font-semibold rounded-xl shadow-sm hover:shadow-md transition-all duration-150 active:scale-[0.98] whitespace-nowrap"
                                        >
                                          <Check size={14} strokeWidth={2.5} />
                                          Confirm
                                        </button>
                                      ))}
                                    </>
                                  )}
                                </div>
                              </div>
                              
                              {/* EVIDENCE - Secondary (what triggered this) */}
                              <p className={`mt-1.5 text-sm leading-relaxed flex flex-wrap items-center gap-1.5 ${matchStatus ? 'text-gray-500 dark:text-gray-500' : 'text-gray-600 dark:text-gray-400'}`}>
                                <span className={matchStatus === 'CONFIRMED' ? 'text-emerald-500 dark:text-emerald-400' : matchStatus === 'DISMISSED' ? 'text-gray-400 dark:text-gray-500' : 'text-emerald-500 dark:text-emerald-400'}>↳</span>
                                <span className="text-gray-500 dark:text-gray-500 whitespace-nowrap">Matched by:</span>
                                <span className={`font-medium ${matchStatus ? 'text-gray-600 dark:text-gray-400' : 'text-gray-700 dark:text-gray-300'}`}>"{evidence}"</span>
                              </p>
                              
                              {/* Status/Confidence on mobile (below evidence) */}
                              <div className="mt-2 sm:hidden">
                                {matchStatus === 'CONFIRMED' ? (
                                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-bold text-emerald-700 dark:text-emerald-300 bg-emerald-100 dark:bg-emerald-900/40 rounded-full">
                                    <Check size={12} strokeWidth={3} />
                                    Confirmed
                                  </span>
                                ) : matchStatus === 'DISMISSED' ? (
                                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-bold text-gray-600 dark:text-gray-400 bg-gray-200 dark:bg-gray-700 rounded-full">
                                    Dismissed
                                  </span>
                                ) : matchCard.confidence !== undefined ? (
                                  renderConfidenceBadge(matchCard.confidence)
                                ) : null}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            }
            
            // Single objective match card
            const isRecentlyClicked = cardState.recentlyClicked.has(card.id);
            const isDisabled = isObjectiveCardDisabled(card);
            const cardStatus = getObjectiveCardStatus(card);
            const { evidence, objective } = (() => {
              const ev = card.title?.replace('Confirm objective match: ', '') || card.title;
              const objMatch = card.subtitle?.match(/Matches ['"](.+)['"]/);
              const obj = objMatch ? objMatch[1] : card.subtitle;
              return { evidence: ev, objective: obj };
            })();
            
            return (
              <div
                key={`${card.id}_${index}_${card.actions?.[0]?.matchId || 'no-match'}`}
                className={`
                  relative overflow-hidden rounded-2xl
                  transition-all duration-200
                  ${cardStatus === 'CONFIRMED' 
                    ? 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/50 ring-1 ring-emerald-100 dark:ring-emerald-900/30' 
                    : cardStatus === 'DISMISSED'
                      ? 'bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700/50 ring-1 ring-gray-100 dark:ring-gray-800/30'
                      : 'bg-white dark:bg-gray-800 border border-emerald-100 dark:border-emerald-900/50 shadow-lg shadow-emerald-100/50 dark:shadow-none ring-1 ring-emerald-50 dark:ring-emerald-900/20'
                  }
                `}
              >
                {/* Decorative background - only show when not actioned */}
                {!cardStatus && (
                  <div className="absolute top-0 right-0 w-48 h-48 bg-gradient-to-br from-emerald-50/60 to-teal-50/40 dark:from-emerald-900/15 dark:to-teal-900/10 rounded-bl-full -mr-12 -mt-12 pointer-events-none" />
                )}
                
                {/* Success overlay for recently clicked */}
                {isRecentlyClicked && (
                  <div className="absolute inset-0 bg-emerald-500/5 backdrop-blur-[2px] flex items-center justify-center z-20 animate-fade-in">
                    <div className="bg-emerald-500 text-white px-5 py-2 rounded-full text-sm font-bold flex items-center gap-2 shadow-lg shadow-emerald-500/25">
                      <Check size={16} strokeWidth={3} />
                      Confirmed!
                    </div>
                  </div>
                )}

                {/* Main content */}
                <div className="relative p-5">
                  <div className="flex items-start gap-5">
                    {/* Icon badge - changes based on status */}
                    <div className="flex-shrink-0">
                      <div className={`
                        w-11 h-11 rounded-xl flex items-center justify-center
                        shadow-sm ring-1
                        ${cardStatus === 'CONFIRMED'
                          ? 'bg-emerald-500 text-white ring-emerald-600/20'
                          : cardStatus === 'DISMISSED'
                            ? 'bg-gray-400 dark:bg-gray-600 text-white ring-gray-500/20'
                            : 'bg-gradient-to-br from-emerald-100 to-emerald-50 dark:from-emerald-900/50 dark:to-emerald-800/30 text-emerald-600 dark:text-emerald-400 ring-emerald-200/50 dark:ring-emerald-700/30'
                        }
                      `}>
                        <Check size={24} strokeWidth={2.5} />
                      </div>
                    </div>
                    
                    {/* Content - Clear hierarchy */}
                    <div className="flex-1 min-w-0">
                      {/* Top row: Objective + Status/Confidence (desktop right-aligned) */}
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <h3 className={`text-base font-bold leading-tight ${cardStatus ? 'text-gray-700 dark:text-gray-300' : 'text-gray-900 dark:text-gray-50'}`}>
                            {objective}
                          </h3>
                        </div>
                        
                        {/* Status badge (when actioned) or Confidence badge (when not) */}
                        <div className="flex-shrink-0 hidden sm:block">
                          {cardStatus === 'CONFIRMED' ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-bold text-emerald-700 dark:text-emerald-300 bg-emerald-100 dark:bg-emerald-900/40 rounded-full">
                              <Check size={12} strokeWidth={3} />
                              Confirmed
                            </span>
                          ) : cardStatus === 'DISMISSED' ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-bold text-gray-600 dark:text-gray-400 bg-gray-200 dark:bg-gray-700 rounded-full">
                              Dismissed
                            </span>
                          ) : card.confidence !== undefined ? (
                            renderConfidenceBadge(card.confidence)
                          ) : null}
                        </div>
                      </div>
                      
                      {/* EVIDENCE - Secondary (what triggered this) */}
                      <p className={`mt-0.5 text-sm leading-relaxed flex flex-wrap items-center gap-1.5 ${cardStatus ? 'text-gray-500 dark:text-gray-500' : 'text-gray-600 dark:text-gray-400'}`}>
                        <span className={cardStatus === 'CONFIRMED' ? 'text-emerald-500 dark:text-emerald-400' : cardStatus === 'DISMISSED' ? 'text-gray-400 dark:text-gray-500' : 'text-emerald-500 dark:text-emerald-400'}>↳</span>
                        <span className="text-gray-500 dark:text-gray-500 whitespace-nowrap">Matched by:</span>
                        <span className={`font-medium ${cardStatus ? 'text-gray-600 dark:text-gray-400' : 'text-gray-700 dark:text-gray-300'}`}>"{evidence}"</span>
                      </p>
                      
                      {/* Status/Confidence on mobile (below evidence) */}
                      <div className="mt-2 sm:hidden">
                        {cardStatus === 'CONFIRMED' ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-bold text-emerald-700 dark:text-emerald-300 bg-emerald-100 dark:bg-emerald-900/40 rounded-full">
                            <Check size={12} strokeWidth={3} />
                            Confirmed
                          </span>
                        ) : cardStatus === 'DISMISSED' ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-bold text-gray-600 dark:text-gray-400 bg-gray-200 dark:bg-gray-700 rounded-full">
                            Dismissed
                          </span>
                        ) : card.confidence !== undefined ? (
                          renderConfidenceBadge(card.confidence)
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action buttons - only show when not actioned */}
                {!cardStatus && Array.isArray(card.actions) && card.actions.length > 0 && !isReadOnly && (
                  <div className="relative px-5 pb-5 pt-1">
                    <div className="flex flex-col sm:flex-row gap-2">
                      {card.actions.map((act) => (
                        <button
                          key={act.id}
                          onClick={(e) => {
                            if (isDisabled) return;
                            e.stopPropagation();
                            e.preventDefault();
                            if (isReadOnly) return;
                            
                            triggerMobilePanelExpansion(card.type, true);
                            
                            if (act?.matchId && (act.type === 'confirm_match' || act.type === 'dismiss_match')) {
                              const newStatus = act.type === 'confirm_match' ? 'CONFIRMED' : 'DISMISSED';
                              setCardState(prev => ({
                                ...prev,
                                matchStatuses: {
                                  ...prev.matchStatuses,
                                  [act.matchId]: { ...(prev.matchStatuses?.[act.matchId] || {}), id: act.matchId, status: newStatus }
                                }
                              }));
                            }
                            onCardClick && onCardClick(act, { ...card, action: act });
                          }}
                          disabled={isDisabled}
                          className={`
                            flex-1 px-5 py-3 text-sm font-semibold rounded-xl min-h-[48px]
                            transition-all duration-150 active:scale-[0.98]
                            ${act.type === 'confirm_match'
                              ? 'bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 text-white shadow-md shadow-emerald-500/20 hover:shadow-lg hover:shadow-emerald-500/25'
                              : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 hover:border-gray-300 dark:hover:border-gray-500 shadow-sm'
                            }
                          `}
                        >
                          <span className="flex items-center justify-center gap-2">
                            {act.type === 'confirm_match' && <Check size={16} strokeWidth={2.5} />}
                            {act.title}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

InteractiveCards.displayName = 'InteractiveCards';

export default memo(InteractiveCards);
